import React, { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Font Awesome Icons for Risk Items
const RISK_ICONS = [
  { value: 'fas fa-clock', label: 'Clock (Aldur)', preview: '🕐' },
  { value: 'fas fa-smoking', label: 'Smoking (Reykingar)', preview: '🚬' },
  { value: 'fas fa-heartbeat', label: 'Heartbeat (Háþrýstingur)', preview: '💓' },
  { value: 'fas fa-tint', label: 'Blood Drop (Sykursýki)', preview: '💧' },
  { value: 'fas fa-vial', label: 'Vial (Blóðfita)', preview: '🧪' },
  { value: 'fas fa-users', label: 'Family (Ættarsaga)', preview: '👥' },
  { value: 'fas fa-weight', label: 'Scale (Ofpyngd)', preview: '⚖️' },
  { value: 'fas fa-weight-hanging', label: 'Weight (Offita)', preview: '🏋️' },
  { value: 'fas fa-running', label: 'Running (Hreyfing)', preview: '🏃' },
  { value: 'fas fa-heart', label: 'Heart', preview: '❤️' },
  { value: 'fas fa-heart-broken', label: 'Broken Heart', preview: '💔' },
  { value: 'fas fa-stethoscope', label: 'Stethoscope', preview: '🩺' },
  { value: 'fas fa-lungs', label: 'Lungs', preview: '🫁' },
  { value: 'fas fa-brain', label: 'Brain', preview: '🧠' },
  { value: 'fas fa-pills', label: 'Pills', preview: '💊' },
  { value: 'fas fa-syringe', label: 'Syringe', preview: '💉' },
  { value: 'fas fa-thermometer', label: 'Thermometer', preview: '🌡️' },
  { value: 'fas fa-apple-alt', label: 'Apple (Diet)', preview: '🍎' },
  { value: 'fas fa-utensils', label: 'Utensils (Food)', preview: '🍴' },
  { value: 'fas fa-wine-glass-alt', label: 'Wine Glass', preview: '🍷' },
  { value: 'fas fa-bed', label: 'Bed (Sleep)', preview: '🛏️' },
  { value: 'fas fa-exclamation-triangle', label: 'Warning', preview: '⚠️' },
  { value: 'fas fa-bolt', label: 'Lightning', preview: '⚡' },
  { value: 'fas fa-dna', label: 'DNA', preview: '🧬' },
  { value: 'fas fa-user-md', label: 'Doctor', preview: '👨‍⚕️' },
  { value: 'fas fa-medkit', label: 'Medical Kit', preview: '🏥' },
];

// Social Media Icons
const SOCIAL_ICONS = [
  { value: 'fab fa-facebook', label: 'Facebook', preview: '📘' },
  { value: 'fab fa-facebook-f', label: 'Facebook F', preview: '📘' },
  { value: 'fab fa-twitter', label: 'Twitter', preview: '🐦' },
  { value: 'fab fa-x-twitter', label: 'X (Twitter)', preview: '✖️' },
  { value: 'fab fa-instagram', label: 'Instagram', preview: '📷' },
  { value: 'fab fa-linkedin', label: 'LinkedIn', preview: '💼' },
  { value: 'fab fa-youtube', label: 'YouTube', preview: '📺' },
  { value: 'fab fa-tiktok', label: 'TikTok', preview: '🎵' },
  { value: 'fab fa-pinterest', label: 'Pinterest', preview: '📌' },
  { value: 'fab fa-snapchat', label: 'Snapchat', preview: '👻' },
  { value: 'fab fa-whatsapp', label: 'WhatsApp', preview: '💬' },
  { value: 'fab fa-telegram', label: 'Telegram', preview: '✈️' },
  { value: 'fab fa-discord', label: 'Discord', preview: '🎮' },
  { value: 'fab fa-reddit', label: 'Reddit', preview: '🤖' },
  { value: 'fab fa-github', label: 'GitHub', preview: '🐱' },
  { value: 'fab fa-spotify', label: 'Spotify', preview: '🎧' },
  { value: 'fas fa-globe', label: 'Website', preview: '🌐' },
  { value: 'fas fa-envelope', label: 'Email', preview: '📧' },
  { value: 'fas fa-phone', label: 'Phone', preview: '📞' },
  { value: 'fas fa-map-marker-alt', label: 'Location', preview: '📍' },
];

// Rich Text Editor Component
const RichTextEditor = ({ value, onChange }) => {
  const iframeRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe) {
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 16px;
              line-height: 1.6;
              padding: 16px;
              min-height: 280px;
              direction: ltr;
              text-align: left;
            }
            body:focus { outline: none; }
            p { margin-bottom: 1em; }
            h2 { font-size: 1.5em; font-weight: bold; margin: 1em 0 0.5em; }
            h3 { font-size: 1.25em; font-weight: bold; margin: 1em 0 0.5em; }
            ul, ol { margin-left: 1.5em; margin-bottom: 1em; }
            a { color: #dc2626; }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body contenteditable="true">${value || ''}</body>
        </html>
      `);
      doc.close();
      
      // Add event listeners
      doc.body.addEventListener('input', () => {
        onChange(doc.body.innerHTML);
      });
      doc.body.addEventListener('blur', () => {
        onChange(doc.body.innerHTML);
      });
    }
  }, []);

  // Update content when value changes externally
  useEffect(() => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument) {
      const doc = iframe.contentDocument;
      if (doc.body && doc.body.innerHTML !== value) {
        doc.body.innerHTML = value || '';
      }
    }
  }, [value]);

  const execCommand = (command, val = null) => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentDocument) {
      iframe.contentWindow.focus();
      iframe.contentDocument.execCommand(command, false, val);
      onChange(iframe.contentDocument.body.innerHTML);
    }
  };

  const insertLink = () => {
    if (linkUrl) {
      execCommand('createLink', linkUrl);
      setShowLinkModal(false);
      setLinkUrl('');
    }
  };

  const insertImage = () => {
    if (imageUrl) {
      execCommand('insertImage', imageUrl);
      setShowImageModal(false);
      setImageUrl('');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Upload to server
      const formData = new FormData();
      formData.append('image', file);
      
      try {
        const res = await fetch(`${BACKEND_URL}/api/upload`, {
          method: 'POST',
          body: formData
        });
        const data = await res.json();
        if (data.url) {
          execCommand('insertImage', data.url);
        } else {
          // Fallback to base64 if upload fails
          const reader = new FileReader();
          reader.onload = (event) => execCommand('insertImage', event.target.result);
          reader.readAsDataURL(file);
        }
      } catch (err) {
        // Fallback to base64
        const reader = new FileReader();
        reader.onload = (event) => execCommand('insertImage', event.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <div className="bg-gray-100 border-b border-gray-300 p-2 flex flex-wrap gap-1">
        <button type="button" onClick={() => execCommand('bold')} className="px-3 py-1 hover:bg-gray-200 rounded font-bold border bg-white">B</button>
        <button type="button" onClick={() => execCommand('italic')} className="px-3 py-1 hover:bg-gray-200 rounded italic border bg-white">I</button>
        <button type="button" onClick={() => execCommand('underline')} className="px-3 py-1 hover:bg-gray-200 rounded underline border bg-white">U</button>
        <span className="border-l border-gray-300 mx-1"></span>
        <button type="button" onClick={() => execCommand('formatBlock', 'h2')} className="px-3 py-1 hover:bg-gray-200 rounded text-sm font-bold border bg-white">H2</button>
        <button type="button" onClick={() => execCommand('formatBlock', 'h3')} className="px-3 py-1 hover:bg-gray-200 rounded text-sm font-bold border bg-white">H3</button>
        <button type="button" onClick={() => execCommand('formatBlock', 'p')} className="px-3 py-1 hover:bg-gray-200 rounded text-sm border bg-white">P</button>
        <span className="border-l border-gray-300 mx-1"></span>
        <button type="button" onClick={() => execCommand('insertUnorderedList')} className="px-3 py-1 hover:bg-gray-200 rounded border bg-white">List</button>
        <button type="button" onClick={() => execCommand('insertOrderedList')} className="px-3 py-1 hover:bg-gray-200 rounded border bg-white">1. List</button>
        <span className="border-l border-gray-300 mx-1"></span>
        <button type="button" onClick={() => setShowLinkModal(true)} className="px-3 py-1 hover:bg-gray-200 rounded border bg-white">Link</button>
        <button type="button" onClick={() => setShowImageModal(true)} className="px-3 py-1 hover:bg-gray-200 rounded border bg-white">Mynd</button>
        <label className="px-3 py-1 hover:bg-gray-200 rounded cursor-pointer border bg-white">
          Hlaða inn mynd
          <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
        </label>
      </div>
      <iframe
        ref={iframeRef}
        className="w-full bg-white"
        style={{ minHeight: '300px', border: 'none' }}
        title="Rich Text Editor"
      />
      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowLinkModal(false)}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Setja inn tengil</h3>
            <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} placeholder="https://example.com" className="w-full px-4 py-2 border rounded-lg mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowLinkModal(false)} className="px-4 py-2 text-gray-600">Haetta vid</button>
              <button onClick={insertLink} className="px-4 py-2 bg-red-600 text-white rounded-lg">Setja inn</button>
            </div>
          </div>
        </div>
      )}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowImageModal(false)}>
          <div className="bg-white rounded-lg p-6 w-96" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold mb-4">Setja inn mynd (URL)</h3>
            <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://example.com/image.jpg" className="w-full px-4 py-2 border rounded-lg mb-4" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowImageModal(false)} className="px-4 py-2 text-gray-600">Haetta vid</button>
              <button onClick={insertImage} className="px-4 py-2 bg-red-600 text-white rounded-lg">Setja inn</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Panel Component
function AdminPanel() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loginError, setLoginError] = useState('');
  const [userIP, setUserIP] = useState('');
  const [activeTab, setActiveTab] = useState('forsida');
  const [siteData, setSiteData] = useState({});
  const [visibility, setVisibility] = useState({});
  const [news, setNews] = useState([]);
  const [stories, setStories] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [risks, setRisks] = useState([]);
  const [donations, setDonations] = useState([]);
  const [magazines, setMagazines] = useState([]);
  // Blog posts
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogConfig, setBlogConfig] = useState({ enabled: true, menuTitle: 'Blog' });
  const [newBlogPost, setNewBlogPost] = useState({ title: '', content: '', image_url: '', link_url: '', show_author: true, created_by: '' });
  const [editingBlogPost, setEditingBlogPost] = useState(null);
  // Sponsors
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsConfig, setSponsorsConfig] = useState({ enabled: true, title: 'Bakhjarlar okkar', subtitle: 'Þökk sé þessum aðilum' });
  const [newSponsor, setNewSponsor] = useState({ name: '', logo_url: '', website: '' });
  const [menuItems, setMenuItems] = useState([
    { id: 1, label: 'Forsíða', path: '/', visible: true, system: true },
    { id: 2, label: 'Fréttir', path: '/news', visible: true, system: true },
    { id: 3, label: 'Sögur', path: '/stories', visible: true, system: true },
    { id: 4, label: 'Uppskriftir', path: '/recipes', visible: true, system: true },
    { id: 5, label: 'Myndir', path: '/gallery', visible: true, system: true },
    { id: 6, label: 'Viðburðir', path: '/events', visible: true, system: true },
    { id: 7, label: 'Blog', path: '/blog', visible: true, system: true },
  ]);
  const [adminUsers, setAdminUsers] = useState([{ id: 1, username: 'admin', password: 'admin', created: '2024-01-01' }]);
  const [footerData, setFooterData] = useState({
    companyName: 'GoRed Ísland',
    tagline: 'Hjartavernd á Íslandi síðan 2009',
    email: 'afgreidsla@hjarta.is',
    phone: '535 5800',
    copyright: '© 2024 GoRed Ísland. Öll réttindi áskilin.',
    linksTitle: 'Flýtitenglar',
    contactTitle: 'Hafa samband',
    socialTitle: 'Fylgstu með'
  });
  const [socialLinks, setSocialLinks] = useState([
    { id: 1, platform: 'Facebook', icon: 'fab fa-facebook', url: 'https://facebook.com/goredis', visible: true },
    { id: 2, platform: 'Instagram', icon: 'fab fa-instagram', url: 'https://instagram.com/goredis', visible: true },
    { id: 3, platform: 'Twitter', icon: 'fab fa-twitter', url: 'https://twitter.com/goredis', visible: true },
  ]);
  const [footerLinks, setFooterLinks] = useState([
    { id: 1, label: 'Forsíða', url: '/', visible: true },
    { id: 2, label: 'Fréttir', url: '/news', visible: true },
    { id: 3, label: 'Sögur', url: '/stories', visible: true },
    { id: 4, label: 'Uppskriftir', url: '/recipes', visible: true },
  ]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [toast, setToast] = useState('');
  const [editModal, setEditModal] = useState(null);
  const [editData, setEditData] = useState({});
  const [newMenuItem, setNewMenuItem] = useState({ label: '', path: '', visible: true });
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '' });
  const [newSocialLink, setNewSocialLink] = useState({ platform: '', icon: 'fab fa-facebook', url: '', visible: true });
  const [newFooterLink, setNewFooterLink] = useState({ label: '', url: '', visible: true });
  const [newPaymentOption, setNewPaymentOption] = useState({ name: '', type: 'info', content: '', visible: true });
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // New feature states
  const [symptomCheckerConfig, setSymptomCheckerConfig] = useState({
    enabled: false,
    title: 'Einkennaskoðari',
    description: 'Athugaðu einkenni hjartasjúkdóma',
    emergencyText: 'Ef þú finnur fyrir þessum einkennum, hringdu strax í 112!',
    symptoms: [
      { id: 1, name: 'Brjóstverkur eða þrýstingur', severity: 'high', description: 'Verkur eða þrýstingur í brjósti sem varir lengur en nokkrar mínútur' },
      { id: 2, name: 'Mæði', severity: 'high', description: 'Öndunarerfiðleikar án þess að hafa reynt á sig' },
      { id: 3, name: 'Verkur sem dreifist', severity: 'high', description: 'Verkur sem leggur út í handlegg, háls, kjálka eða bak' },
      { id: 4, name: 'Ógleði eða uppköst', severity: 'medium', description: 'Ógleði sem kemur með öðrum einkennum' },
      { id: 5, name: 'Kaldur sviti', severity: 'medium', description: 'Skyndilegur kaldur sviti' },
      { id: 6, name: 'Svimi', severity: 'medium', description: 'Sundl eða svimi' },
    ]
  });
  const [aedConfig, setAedConfig] = useState({
    enabled: false,
    title: 'AED Staðsetningar',
    description: 'Finndu næsta hjartastuð',
    locations: [
      { id: 1, name: 'Landspítali Fossvogur', address: 'Fossvogur, 108 Reykjavík', lat: 64.1186, lng: -21.8974, phone: '543 1000' },
      { id: 2, name: 'Kringlan', address: 'Kringlan 4-12, 103 Reykjavík', lat: 64.1315, lng: -21.8948, phone: '' },
      { id: 3, name: 'Smáralind', address: 'Hagasmári 1, 201 Kópavogur', lat: 64.1043, lng: -21.8856, phone: '' },
    ]
  });
  const [newsletterConfig, setNewsletterConfig] = useState({
    enabled: false,
    title: 'Fréttabréf',
    description: 'Skráðu þig á fréttabréf GoRed',
    buttonText: 'Skrá mig',
    successMessage: 'Takk fyrir að skrá þig!',
    subscribers: []
  });
  const [newSymptom, setNewSymptom] = useState({ name: '', severity: 'medium', description: '' });
  const [newAedLocation, setNewAedLocation] = useState({ name: '', address: '', lat: '', lng: '', phone: '' });
  
  // Risk section config
  const [riskSectionConfig, setRiskSectionConfig] = useState({
    title: 'Áhættuþættir',
    subtitle: 'Þekktu áhættuþættina og verndaðu hjartað þitt',
    useGradient: true
  });
  
  // About section config (Afhverju GoRed?)
  const [aboutSectionConfig, setAboutSectionConfig] = useState({
    title: 'Afhverju GoRed?',
    useGradient: true,
    text: '<p>GoRed átakið miðar að því að fræða konur um áhættuþætti og einkenni hjarta- og æðasjúkdóma. Aukin vitund kvenna um áhættuþættina hefur einnig óbein áhrif á lífsstíl karla og ungmenna.</p><p>Um er að ræða alþjóðlegt langtímaverkefni sem hófst á Íslandi frá árinu 2009.</p>',
    imageUrl: ''
  });
  
  // Contact info config
  const [contactInfo, setContactInfo] = useState({
    email: '',
    phone: '',
    address: ''
  });
  
  // About Us page config
  const [aboutUsPageConfig, setAboutUsPageConfig] = useState({
    menuTitle: 'GoRed Ísland',
    enabled: true,
    content: '<h2>Um GoRed Ísland</h2><p>GoRed er alþjóðlegt átaksverkefni sem miðar að því að vekja athygli á hjarta- og æðasjúkdómum hjá konum.</p>'
  });
  
  // Cookie consent config
  const [cookieConfig, setCookieConfig] = useState({
    enabled: true,
    title: 'Vafrakökur',
    description: 'Við notum vafrakökur til að bæta upplifun þína á vefsíðunni. Sumar vafrakökur eru nauðsynlegar fyrir virkni síðunnar, á meðan aðrar hjálpa okkur að skilja hvernig þú notar síðuna.',
    necessaryTitle: 'Nauðsynlegar',
    necessaryDesc: 'Þessar vafrakökur eru nauðsynlegar fyrir grunnvirkni vefsíðunnar og ekki er hægt að slökkva á þeim.',
    analyticsTitle: 'Tölfræði',
    analyticsDesc: 'Þessar vafrakökur hjálpa okkur að skilja hvernig gestir nota vefsíðuna með því að safna nafnlausum upplýsingum.',
    marketingTitle: 'Markaðssetning',
    marketingDesc: 'Þessar vafrakökur eru notaðar til að sýna þér viðeigandi auglýsingar á öðrum vefsíðum.',
    acceptAllText: 'Samþykkja allt',
    acceptNecessaryText: 'Aðeins nauðsynlegar',
    savePreferencesText: 'Vista stillingar',
    privacyLinkText: 'Persónuverndarstefna'
  });
  
  // Privacy policy config
  const [privacyConfig, setPrivacyConfig] = useState({
    enabled: true,
    title: 'Persónuverndarstefna',
    lastUpdated: new Date().toISOString().split('T')[0],
    content: `<h2>Persónuverndarstefna</h2>
<p>Þessi persónuverndarstefna lýsir hvernig við söfnum, notum og verndum persónuupplýsingar þínar.</p>

<h3>1. Hvaða upplýsingum söfnum við?</h3>
<p>Við söfnum upplýsingum sem þú veitir okkur beint, svo sem þegar þú skráir þig á fréttabréf eða hefur samband við okkur.</p>

<h3>2. Hvernig notum við upplýsingarnar?</h3>
<p>Við notum upplýsingarnar til að:</p>
<ul>
<li>Veita þér þjónustu og upplýsingar</li>
<li>Bæta vefsíðuna okkar</li>
<li>Senda þér fréttabréf (ef þú hefur skráð þig)</li>
</ul>

<h3>3. Vafrakökur</h3>
<p>Við notum vafrakökur til að bæta upplifun þína. Þú getur stjórnað stillingum vafrakaka í vafranum þínum.</p>

<h3>4. Réttindi þín</h3>
<p>Þú átt rétt á að fá aðgang að, leiðrétta eða eyða persónuupplýsingum þínum. Hafðu samband við okkur ef þú vilt nýta þessi réttindi.</p>

<h3>5. Hafa samband</h3>
<p>Ef þú hefur spurningar um persónuverndarstefnu okkar, vinsamlegast hafðu samband.</p>`
  });
  
  // Custom pages for menu items
  const [customPages, setCustomPages] = useState([]);
  const [editingPage, setEditingPage] = useState(null);
  
  // Gallery state
  const [galleries, setGalleries] = useState([]);
  const [editingGallery, setEditingGallery] = useState(null);
  const [newGallery, setNewGallery] = useState({ name: '', description: '', images: [] });

  // Events state
  const [events, setEvents] = useState([]);
  const [eventsConfig, setEventsConfig] = useState({ enabled: false, menuTitle: 'Viðburðir' });
  const [editingEvent, setEditingEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ 
    title: '', 
    date: '', 
    time: '', 
    description: '', 
    image_url: '', 
    location_name: '',
    latitude: 64.1466, 
    longitude: -21.9426,
    website: '',
    show_author: true,
    created_by: ''
  });
  
  // Content author settings
  const [showAuthorSettings, setShowAuthorSettings] = useState({ news: true, stories: true, recipes: true, galleries: true });
  
  // Sidebar state for admin panel
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState(['content', 'settings']);
  
  const navigate = useNavigate();

  useEffect(() => {
    const session = sessionStorage.getItem('gored_admin');
    const savedUser = sessionStorage.getItem('gored_user');
    if (session === 'true') { 
      setIsLoggedIn(true); 
      if (savedUser) setLoggedInUser(JSON.parse(savedUser));
      loadAllData(); 
    }
    // Fetch user IP
    fetch('https://api.ipify.org?format=json')
      .then(r => r.json())
      .then(data => setUserIP(data.ip))
      .catch(() => setUserIP('Unknown'));
    // Load admin users for login validation
    loadAdminUsers();
  }, []);

  // Load admin users before login (for validation)
  const loadAdminUsers = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/settings`);
      const settings = await res.json();
      if (settings.adminUsers) {
        setAdminUsers(JSON.parse(settings.adminUsers));
      }
    } catch (e) { console.log('Could not load admin users'); }
  };

  const loadAllData = async () => {
    try {
      const [settings, vis, newsData, storiesData, recipesData, risksData, donData, magData] = await Promise.all([
        fetch(`${BACKEND_URL}/api/settings`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/visibility`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/news`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/stories`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/recipes`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/risks`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/donations`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/magazines`).then(r => r.json())
      ]);
      setSiteData(settings);
      setVisibility(vis);
      setNews(Array.isArray(newsData) ? newsData : []);
      setStories(Array.isArray(storiesData) ? storiesData : []);
      setRecipes(Array.isArray(recipesData) ? recipesData : []);
      setRisks(Array.isArray(risksData) ? risksData : []);
      setDonations(Array.isArray(donData) ? donData : []);
      setMagazines(Array.isArray(magData) ? magData : []);
      
      // Load all config from database settings
      if (settings.menuItems) setMenuItems(JSON.parse(settings.menuItems));
      if (settings.footerData) setFooterData(JSON.parse(settings.footerData));
      if (settings.socialLinks) setSocialLinks(JSON.parse(settings.socialLinks));
      if (settings.footerLinks) setFooterLinks(JSON.parse(settings.footerLinks));
      if (settings.paymentOptions) setPaymentOptions(JSON.parse(settings.paymentOptions));
      if (settings.symptomCheckerConfig) setSymptomCheckerConfig(JSON.parse(settings.symptomCheckerConfig));
      if (settings.aedConfig) setAedConfig(JSON.parse(settings.aedConfig));
      if (settings.newsletterConfig) setNewsletterConfig(JSON.parse(settings.newsletterConfig));
      if (settings.riskSectionConfig) setRiskSectionConfig(JSON.parse(settings.riskSectionConfig));
      if (settings.aboutSectionConfig) setAboutSectionConfig(JSON.parse(settings.aboutSectionConfig));
      if (settings.contactInfo) setContactInfo(JSON.parse(settings.contactInfo));
      if (settings.aboutUsPageConfig) setAboutUsPageConfig(JSON.parse(settings.aboutUsPageConfig));
      if (settings.adminUsers) setAdminUsers(JSON.parse(settings.adminUsers));
      if (settings.customPages) setCustomPages(JSON.parse(settings.customPages));
      if (settings.galleries) setGalleries(JSON.parse(settings.galleries));
      if (settings.events) setEvents(JSON.parse(settings.events));
      if (settings.eventsConfig) setEventsConfig(JSON.parse(settings.eventsConfig));
      if (settings.showAuthorSettings) setShowAuthorSettings(JSON.parse(settings.showAuthorSettings));
      if (settings.blogPosts) setBlogPosts(JSON.parse(settings.blogPosts));
      if (settings.blogConfig) setBlogConfig(JSON.parse(settings.blogConfig));
      if (settings.sponsors) setSponsors(JSON.parse(settings.sponsors));
      if (settings.sponsorsConfig) setSponsorsConfig(JSON.parse(settings.sponsorsConfig));
    } catch (e) { console.error('Error:', e); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      // Check credentials via API (reads from admin_users SQL table)
      const res = await fetch(`${BACKEND_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      
      if (res.ok && data.success) {
        setIsLoggedIn(true);
        setLoggedInUser(data.user || { username: username });
        sessionStorage.setItem('gored_admin', 'true');
        sessionStorage.setItem('gored_user', JSON.stringify(data.user || { username: username }));
        loadAllData(); 
        setLoginError('');
      } else {
        setLoginError('Rangt notandanafn eða lykilorð. / Wrong username or password.'); 
      }
    } catch (e) {
      // Fallback to local check if API fails
      const validUser = adminUsers.find(u => 
        (u.email === username || u.username === username) && u.password === password
      );
      if (validUser) {
        setIsLoggedIn(true);
        setLoggedInUser(validUser);
        sessionStorage.setItem('gored_admin', 'true');
        sessionStorage.setItem('gored_user', JSON.stringify(validUser));
        loadAllData(); 
        setLoginError('');
      } else { 
        setLoginError('Rangt notandanafn eða lykilorð. / Wrong username or password.'); 
      }
    }
  };

  const handleLogout = () => { 
    sessionStorage.removeItem('gored_admin'); 
    sessionStorage.removeItem('gored_user');
    setIsLoggedIn(false); 
    setLoggedInUser(null);
    navigate('/'); 
  };
  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };
  
  // Helper function to save any config to database
  const saveConfigToDb = async (key, value) => {
    try {
      await fetch(`${BACKEND_URL}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: JSON.stringify(value) })
      });
      return true;
    } catch (e) {
      console.error('Save error:', e);
      return false;
    }
  };
  
  const saveSettings = async () => {
    try {
      // Only save the specific hero/logo settings to avoid overwriting other configs
      const heroSettings = {
        logoText: siteData.logoText,
        heroTitle: siteData.heroTitle,
        heroSubtitle: siteData.heroSubtitle,
        heroHighlight: siteData.heroHighlight,
        heroDesc: siteData.heroDesc
      };
      await fetch(`${BACKEND_URL}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(heroSettings)
      });
      showToast('Stillingar vistaðar!');
    } catch (e) {
      console.error('Settings save error:', e);
      showToast('Villa við vistun!');
    }
  };

  const openEditModal = (type, item = null) => {
    setEditModal(type);
    setEditData(item || { 
      title: '', 
      content: '', 
      image_url: '', 
      is_published: true, 
      icon: 'fas fa-heart', 
      description: '',
      show_author: true,
      created_by: loggedInUser?.username || 'admin'
    });
  };
  const closeEditModal = () => { setEditModal(null); setEditData({}); };

  const saveItem = async () => {
    const now = new Date().toISOString();
    const typeMap = { news: 'news', story: 'stories', recipe: 'recipes', risk: 'risks' };
    const endpoint = typeMap[editModal];
    
    // Add author info if creating new item
    const itemData = editData.id ? editData : { 
      ...editData, 
      created_by: loggedInUser?.username || 'admin',
      show_author: editData.show_author !== false
    };
    
    try {
      if (editData.id) {
        // Update existing item
        await fetch(`${BACKEND_URL}/api/${endpoint}/${editData.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData)
        });
        if (editModal === 'news') setNews(news.map(n => n.id === editData.id ? { ...itemData, updated_at: now } : n));
        else if (editModal === 'story') setStories(stories.map(s => s.id === editData.id ? { ...itemData, updated_at: now } : s));
        else if (editModal === 'recipe') setRecipes(recipes.map(r => r.id === editData.id ? { ...itemData, updated_at: now } : r));
        else if (editModal === 'risk') setRisks(risks.map(r => r.id === editData.id ? itemData : r));
      } else {
        // Create new item
        const res = await fetch(`${BACKEND_URL}/api/${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(itemData)
        });
        const result = await res.json();
        const newItem = { ...itemData, id: result.id, created_at: now, updated_at: now };
        if (editModal === 'news') setNews([newItem, ...news]);
        else if (editModal === 'story') setStories([newItem, ...stories]);
        else if (editModal === 'recipe') setRecipes([newItem, ...recipes]);
        else if (editModal === 'risk') setRisks([...risks, newItem]);
      }
      showToast('Vistað!');
    } catch (e) {
      console.error('Save error:', e);
      showToast('Villa við vistun!');
    }
    closeEditModal();
  };

  const deleteItem = async (type, id) => {
    if (window.confirm('Ertu viss?')) {
      const typeMap = { news: 'news', story: 'stories', recipe: 'recipes', risk: 'risks' };
      const endpoint = typeMap[type];
      
      try {
        await fetch(`${BACKEND_URL}/api/${endpoint}/${id}`, { method: 'DELETE' });
        if (type === 'news') setNews(news.filter(n => n.id !== id));
        if (type === 'story') setStories(stories.filter(s => s.id !== id));
        if (type === 'recipe') setRecipes(recipes.filter(r => r.id !== id));
        if (type === 'risk') setRisks(risks.filter(r => r.id !== id));
        showToast('Eytt!');
      } catch (e) {
        console.error('Delete error:', e);
        showToast('Villa við eyðingu!');
      }
    }
  };

  // Menu management
  const saveMenuItems = async () => { 
    if (await saveConfigToDb('menuItems', menuItems)) {
      showToast('Valmynd vistuð!'); 
    } else {
      showToast('Villa við vistun!');
    }
  };
  const addMenuItem = async () => {
    if (newMenuItem.label && newMenuItem.path) {
      const path = newMenuItem.path.startsWith('/') ? newMenuItem.path : '/' + newMenuItem.path;
      const newItem = { ...newMenuItem, id: Date.now(), path, system: false, hasPage: true };
      const newPage = { id: newItem.id, path, title: newMenuItem.label, content: '<p>Innihald síðu...</p>' };
      setMenuItems([...menuItems, newItem]);
      const updatedPages = [...customPages, newPage];
      setCustomPages(updatedPages);
      await saveConfigToDb('customPages', updatedPages);
      setNewMenuItem({ label: '', path: '', visible: true }); 
      showToast('Bætt við!');
    }
  };
  const toggleMenuVisibility = async (id) => {
    const updated = menuItems.map(item => item.id === id ? { ...item, visible: !item.visible } : item);
    setMenuItems(updated);
    await saveConfigToDb('menuItems', updated);
    showToast('Sýnileiki uppfærður!');
  };
  const deleteMenuItem = async (id) => {
    const item = menuItems.find(m => m.id === id);
    if (item?.system) { showToast('Ekki hægt að eyða kerfisatriði'); return; }
    if (window.confirm('Ertu viss?')) { 
      setMenuItems(menuItems.filter(m => m.id !== id)); 
      const updatedPages = customPages.filter(p => p.id !== id);
      setCustomPages(updatedPages);
      await saveConfigToDb('customPages', updatedPages);
      showToast('Eytt!'); 
    }
  };
  const updateMenuItemLabel = (id, newLabel) => {
    setMenuItems(menuItems.map(item => item.id === id ? { ...item, label: newLabel } : item));
  };
  const saveCustomPage = async (pageData) => {
    const updatedPages = customPages.map(p => p.id === pageData.id ? pageData : p);
    setCustomPages(updatedPages);
    if (await saveConfigToDb('customPages', updatedPages)) {
      showToast('Síða vistuð!');
      setEditingPage(null);
    }
  };
  
  // Gallery management
  const saveGalleries = async () => {
    if (await saveConfigToDb('galleries', galleries)) {
      showToast('Myndasöfn vistuð!');
    }
  };
  const addGallery = async () => {
    if (newGallery.name) {
      const gallery = { 
        ...newGallery, 
        id: Date.now(), 
        createdAt: new Date().toISOString(),
        created_by: loggedInUser?.username || 'admin',
        show_author: true
      };
      const updated = [...galleries, gallery];
      setGalleries(updated);
      await saveConfigToDb('galleries', updated);
      setNewGallery({ name: '', description: '', images: [] });
      showToast('Myndasafn búið til!');
    }
  };
  const deleteGallery = async (id) => {
    if (window.confirm('Ertu viss um að eyða þessu myndasafni?')) {
      const updated = galleries.filter(g => g.id !== id);
      setGalleries(updated);
      await saveConfigToDb('galleries', updated);
      showToast('Eytt!');
    }
  };
  const updateGallery = async (galleryData) => {
    const updated = galleries.map(g => g.id === galleryData.id ? galleryData : g);
    setGalleries(updated);
    await saveConfigToDb('galleries', updated);
    setEditingGallery(null);
    showToast('Myndasafn vistað!');
  };
  const addImageToGallery = async (galleryId, imageUrl) => {
    const updated = galleries.map(g => {
      if (g.id === galleryId) {
        return { ...g, images: [...(g.images || []), { id: Date.now(), url: imageUrl }] };
      }
      return g;
    });
    setGalleries(updated);
    await saveConfigToDb('galleries', updated);
  };
  const removeImageFromGallery = async (galleryId, imageId) => {
    const updated = galleries.map(g => {
      if (g.id === galleryId) {
        return { ...g, images: (g.images || []).filter(img => img.id !== imageId) };
      }
      return g;
    });
    setGalleries(updated);
    await saveConfigToDb('galleries', updated);
  };

  // User management
  const addUser = async () => {
    if (newUser.username && newUser.email && newUser.password) {
      const newUserData = { 
        id: Date.now(), 
        username: newUser.username, 
        email: newUser.email, 
        password: newUser.password, 
        created: new Date().toISOString().split('T')[0] 
      };
      const updated = [...adminUsers, newUserData];
      setAdminUsers(updated); 
      await saveConfigToDb('adminUsers', updated);
      // Also save to database via API
      try {
        await fetch(`${BACKEND_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newUserData)
        });
      } catch (e) { console.log('User API save skipped'); }
      setNewUser({ username: '', email: '', password: '' }); 
      showToast('Notandi búinn til!');
    } else {
      showToast('Vinsamlegast fylltu út alla reiti');
    }
  };
  const deleteUser = async (id) => {
    if (adminUsers.length <= 1) { showToast('Verður að vera að minnsta kosti einn notandi'); return; }
    if (window.confirm('Ertu viss?')) {
      const updated = adminUsers.filter(u => u.id !== id);
      setAdminUsers(updated); 
      await saveConfigToDb('adminUsers', updated);
      showToast('Eytt!');
    }
  };

  // Magazine management
  const addMagazine = async (mag) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/magazines`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(mag)
      });
      if (res.ok) {
        const newMag = await res.json();
        setMagazines([...magazines, newMag.id ? newMag : { ...mag, id: Date.now() }]);
        showToast('Tímarit bætt við!');
      }
    } catch (e) {
      // Fallback to local state
      setMagazines([...magazines, { ...mag, id: Date.now() }]);
      showToast('Tímarit bætt við!');
    }
  };
  const deleteMagazine = async (id) => {
    if (window.confirm('Ertu viss um að þú viljir eyða þessu tímariti?')) {
      try {
        await fetch(`${BACKEND_URL}/api/magazines/${id}`, { method: 'DELETE' });
      } catch (e) { /* ignore */ }
      setMagazines(magazines.filter(m => m.id !== id));
      showToast('Tímariti eytt!');
    }
  };

  // Footer management
  const saveFooter = async () => {
    const results = await Promise.all([
      saveConfigToDb('footerData', footerData),
      saveConfigToDb('socialLinks', socialLinks),
      saveConfigToDb('footerLinks', footerLinks)
    ]);
    if (results.every(r => r)) {
      showToast('Fótur vistaður!');
    } else {
      showToast('Villa við vistun!');
    }
  };
  const addSocialLink = () => {
    if (newSocialLink.platform && newSocialLink.url) {
      setSocialLinks([...socialLinks, { ...newSocialLink, id: Date.now() }]);
      setNewSocialLink({ platform: '', icon: 'fab fa-facebook', url: '', visible: true }); showToast('Bætt við!');
    }
  };
  const deleteSocialLink = (id) => { setSocialLinks(socialLinks.filter(s => s.id !== id)); showToast('Eytt!'); };
  const addFooterLink = () => {
    if (newFooterLink.label && newFooterLink.url) {
      setFooterLinks([...footerLinks, { ...newFooterLink, id: Date.now() }]);
      setNewFooterLink({ label: '', url: '', visible: true }); showToast('Bætt við!');
    }
  };
  const deleteFooterLink = (id) => { setFooterLinks(footerLinks.filter(l => l.id !== id)); showToast('Eytt!'); };

  // Payment options
  const addPaymentOption = async () => {
    if (newPaymentOption.name) {
      const updated = [...paymentOptions, { ...newPaymentOption, id: Date.now() }];
      setPaymentOptions(updated); 
      await saveConfigToDb('paymentOptions', updated);
      setNewPaymentOption({ name: '', type: 'info', content: '', visible: true }); showToast('Greiðslumáti bætt við!');
    }
  };
  const deletePaymentOption = async (id) => {
    const updated = paymentOptions.filter(p => p.id !== id);
    setPaymentOptions(updated); 
    await saveConfigToDb('paymentOptions', updated);
    showToast('Eytt!');
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 to-purple-800 flex items-center justify-center">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md mx-4">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Randalin</h1>
            <p className="text-gray-600 mb-4">Vefumsjónarkerfi</p>
            <p className="text-xs text-gray-400">IP: {userIP || 'Loading...'}</p>
          </div>
          {loginError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              <p className="font-bold mb-1">Aðgangi hafnað</p>
              <p className="text-sm">{loginError}</p>
            </div>
          )}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-gray-700 font-medium mb-2">Netfang</label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Sláðu inn netfang" required />
            </div>
            <div>
              <label className="block text-gray-700 font-medium mb-2">Lykilorð</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg" placeholder="Sláðu inn lykilorð" required />
            </div>
            <button type="submit" className="w-full bg-indigo-600 text-white py-3 rounded-lg font-bold hover:bg-indigo-700">Innskrá</button>
          </form>
          <div className="mt-6 text-center">
            <button onClick={() => navigate('/')} className="text-indigo-600 hover:text-indigo-700">← Til baka á forsíðu</button>
          </div>
        </div>
      </div>
    );
  }

  const sidebarGroups = [
    {
      id: 'content',
      label: 'Efni',
      icon: 'fas fa-edit',
      items: [
        { id: 'news', label: 'Fréttir', icon: 'fas fa-newspaper' },
        { id: 'stories', label: 'Sögur', icon: 'fas fa-book-open' },
        { id: 'recipes', label: 'Uppskriftir', icon: 'fas fa-utensils' },
        { id: 'events', label: 'Viðburðir', icon: 'fas fa-calendar-alt' },
        { id: 'blog', label: 'Blog', icon: 'fas fa-blog' },
        { id: 'risks', label: 'Áhættuþættir', icon: 'fas fa-exclamation-triangle' },
        { id: 'sponsors', label: 'Bakhjarlar', icon: 'fas fa-handshake' },
      ]
    },
    {
      id: 'media',
      label: 'Miðlar',
      icon: 'fas fa-images',
      items: [
        { id: 'gallery', label: 'Myndasöfn', icon: 'fas fa-photo-video' },
        { id: 'magazines', label: 'Veftímarit', icon: 'fas fa-book' },
      ]
    },
    {
      id: 'settings',
      label: 'Stillingar',
      icon: 'fas fa-cog',
      items: [
        { id: 'forsida', label: 'Forsíða', icon: 'fas fa-home' },
        { id: 'menu', label: 'Valmynd', icon: 'fas fa-bars' },
        { id: 'footer', label: 'Fótur', icon: 'fas fa-shoe-prints' },
        { id: 'aboutus', label: 'Um okkur', icon: 'fas fa-info-circle' },
      ]
    },
    {
      id: 'tools',
      label: 'Verkfæri',
      icon: 'fas fa-tools',
      items: [
        { id: 'symptom', label: 'Einkennaskoðari', icon: 'fas fa-heartbeat' },
        { id: 'aed', label: 'AED Kort', icon: 'fas fa-map-marker-alt' },
        { id: 'newsletter', label: 'Fréttabréf', icon: 'fas fa-envelope' },
        { id: 'donations', label: 'Styrkir', icon: 'fas fa-hand-holding-heart' },
      ]
    },
    {
      id: 'system',
      label: 'Kerfi',
      icon: 'fas fa-server',
      items: [
        { id: 'users', label: 'Notendur', icon: 'fas fa-users' },
        { id: 'cookies', label: 'Vafrakökur', icon: 'fas fa-cookie-bite' },
        { id: 'privacy', label: 'Persónuvernd', icon: 'fas fa-shield-alt' },
        { id: 'system', label: 'Kerfisupplýsingar', icon: 'fas fa-info' },
      ]
    },
  ];
  
  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => 
      prev.includes(groupId) 
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-16'} bg-indigo-900 text-white transition-all duration-300 flex flex-col fixed h-full z-40`}>
        {/* Sidebar Header */}
        <div className="p-4 border-b border-indigo-800 flex items-center justify-between">
          {sidebarOpen && (
            <div>
              <h1 className="text-lg font-bold">Randalin</h1>
              <p className="text-indigo-300 text-xs">Vefumsjónarkerfi</p>
            </div>
          )}
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 hover:bg-indigo-800 rounded-lg">
            <i className={`fas ${sidebarOpen ? 'fa-chevron-left' : 'fa-chevron-right'}`}></i>
          </button>
        </div>
        
        {/* User Info */}
        {sidebarOpen && (
          <div className="p-4 border-b border-indigo-800">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-700 rounded-full flex items-center justify-center">
                <i className="fas fa-user"></i>
              </div>
              <div>
                <p className="font-medium text-sm">{loggedInUser?.username || 'Admin'}</p>
                <p className="text-indigo-300 text-xs">Stjórnandi</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Navigation Groups */}
        <nav className="flex-1 overflow-y-auto py-4">
          {sidebarGroups.map(group => (
            <div key={group.id} className="mb-2">
              <button 
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-indigo-800 transition ${!sidebarOpen ? 'justify-center' : ''}`}
              >
                <i className={`${group.icon} ${sidebarOpen ? 'w-5' : ''}`}></i>
                {sidebarOpen && (
                  <>
                    <span className="flex-1 font-medium text-sm">{group.label}</span>
                    <i className={`fas fa-chevron-${expandedGroups.includes(group.id) ? 'down' : 'right'} text-xs text-indigo-400`}></i>
                  </>
                )}
              </button>
              {sidebarOpen && expandedGroups.includes(group.id) && (
                <div className="ml-4 mt-1 space-y-1">
                  {group.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left rounded-l-lg transition text-sm ${
                        activeTab === item.id 
                          ? 'bg-white text-indigo-900 font-medium' 
                          : 'text-indigo-200 hover:bg-indigo-800 hover:text-white'
                      }`}
                    >
                      <i className={`${item.icon} w-4 text-center`}></i>
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
        
        {/* Sidebar Footer */}
        <div className="p-4 border-t border-indigo-800">
          <a href="/" target="_blank" className={`flex items-center gap-3 text-indigo-300 hover:text-white transition mb-3 ${!sidebarOpen ? 'justify-center' : ''}`}>
            <i className="fas fa-external-link-alt"></i>
            {sidebarOpen && <span className="text-sm">Skoða síðu</span>}
          </a>
          <button onClick={handleLogout} className={`w-full flex items-center gap-3 bg-indigo-700 hover:bg-indigo-600 px-4 py-2 rounded-lg transition ${!sidebarOpen ? 'justify-center' : ''}`}>
            <i className="fas fa-sign-out-alt"></i>
            {sidebarOpen && <span className="text-sm">Útskrá</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-16'} transition-all duration-300`}>
        {/* Top Header */}
        <header className="bg-white shadow-sm sticky top-0 z-30">
          <div className="px-6 py-4 flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                {sidebarGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || 'Stjórnborð'}
              </h2>
              <p className="text-gray-500 text-sm">
                {sidebarGroups.find(g => g.items.some(i => i.id === activeTab))?.label || 'Velkomin'}
              </p>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-500">
                <i className="fas fa-clock mr-2"></i>
                {new Date().toLocaleDateString('is-IS', { weekday: 'long', day: 'numeric', month: 'long' })}
              </span>
            </div>
          </div>
        </header>

        <main className="p-6">
        {/* Forsíða Tab */}
        {activeTab === 'forsida' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Stillingar forsíðu</h2>
              <div className="space-y-4">
                <div><label className="block text-gray-700 font-medium mb-2">Lógó texti</label>
                  <input type="text" value={siteData.logoText || ''} onChange={(e) => setSiteData({...siteData, logoText: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                
                <h3 className="font-bold text-gray-800 pt-4 border-t">Hero hluti</h3>
                <p className="text-sm text-gray-500">Fyrirsögn birtist sem: <span className="font-mono bg-gray-100 px-2 py-1 rounded">[Titill] [Undirtexti] [Auðkenndur texti] ❤️</span></p>
                
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Hero titill (rauður)</label>
                    <input type="text" value={siteData.heroTitle || ''} onChange={(e) => setSiteData({...siteData, heroTitle: e.target.value})} placeholder="GoRed" className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Undirtexti (grár)</label>
                    <input type="text" value={siteData.heroSubtitle || ''} onChange={(e) => setSiteData({...siteData, heroSubtitle: e.target.value})} placeholder="fyrir" className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Auðkenndur texti (rauður)</label>
                    <input type="text" value={siteData.heroHighlight || ''} onChange={(e) => setSiteData({...siteData, heroHighlight: e.target.value})} placeholder="konur" className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Hero lýsing</label>
                  <textarea value={siteData.heroDesc || ''} onChange={(e) => setSiteData({...siteData, heroDesc: e.target.value})} className="w-full px-4 py-3 border rounded-lg" rows={3} />
                </div>
                <button onClick={saveSettings} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista stillingar</button>
              </div>
            </div>

            {/* Afhverju GoRed? Section */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Afhverju GoRed? - Hluti</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Titill</label>
                    <input type="text" value={aboutSectionConfig.title} onChange={(e) => setAboutSectionConfig({...aboutSectionConfig, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" checked={aboutSectionConfig.useGradient} onChange={(e) => setAboutSectionConfig({...aboutSectionConfig, useGradient: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-3" />
                      <span className="font-medium">Nota rautt-til-bleikt litafall á titil</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Texti</label>
                  <RichTextEditor value={aboutSectionConfig.text} onChange={(val) => setAboutSectionConfig({...aboutSectionConfig, text: val})} />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Mynd</label>
                  <div className="flex gap-4 items-start">
                    <div className="flex-1">
                      <input type="url" value={aboutSectionConfig.imageUrl} onChange={(e) => setAboutSectionConfig({...aboutSectionConfig, imageUrl: e.target.value})} placeholder="Sláðu inn URL eða hladdu upp mynd" className="w-full px-4 py-3 border rounded-lg mb-2" />
                      <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                        <i className="fas fa-upload mr-2"></i>Hlaða upp mynd
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('image', file);
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                              const data = await res.json();
                              if (data.url) setAboutSectionConfig({...aboutSectionConfig, imageUrl: data.url});
                            } catch (err) {
                              // Fallback to base64
                              const reader = new FileReader();
                              reader.onload = (ev) => setAboutSectionConfig({...aboutSectionConfig, imageUrl: ev.target.result});
                              reader.readAsDataURL(file);
                            }
                          }
                        }} />
                      </label>
                    </div>
                    {aboutSectionConfig.imageUrl && <img src={aboutSectionConfig.imageUrl} alt="Forskoðun" className="h-32 object-cover rounded-lg border" />}
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Forskoðun á titli:</h4>
                  <span className={`text-2xl font-bold ${aboutSectionConfig.useGradient ? 'bg-gradient-to-r from-red-600 to-pink-500 bg-clip-text text-transparent' : 'text-gray-900'}`}>{aboutSectionConfig.title}</span>
                </div>
                <button onClick={async () => { await saveConfigToDb('aboutSectionConfig', aboutSectionConfig); showToast('Afhverju GoRed hluti vistaður!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista Afhverju GoRed</button>
              </div>
            </div>
          </div>
        )}

        {/* News Tab */}
        {activeTab === 'news' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">📰 Fréttir</h2>
              <button onClick={() => openEditModal('news')} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">➕ Ný frétt</button>
            </div>
            <div className="space-y-4">
              {news.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {item.image_url && <img src={item.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />}
                    <div><h3 className="font-bold text-gray-900">{item.title}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.is_published ? 'Birt' : 'Drög'}</span></div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openEditModal('news', item)} className="text-blue-600 p-2">✏️</button>
                    <button onClick={() => deleteItem('news', item.id)} className="text-red-600 p-2">🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stories Tab */}
        {activeTab === 'stories' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">📖 Sögur</h2>
              <button onClick={() => openEditModal('story')} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">➕ Ný saga</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stories.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow overflow-hidden">
                  {item.image_url && <img src={item.image_url} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <div className="flex justify-between items-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.is_published ? 'Birt' : 'Drög'}</span>
                      <div className="flex space-x-2">
                        <button onClick={() => openEditModal('story', item)} className="text-blue-600">✏️</button>
                        <button onClick={() => deleteItem('story', item.id)} className="text-red-600">🗑️</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recipes Tab */}
        {activeTab === 'recipes' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900">🍳 Uppskriftir</h2>
              <button onClick={() => openEditModal('recipe')} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">➕ Ný uppskrift</button>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recipes.map(item => (
                <div key={item.id} className="bg-white rounded-xl shadow overflow-hidden">
                  {item.image_url && <img src={item.image_url} alt="" className="w-full h-40 object-cover" />}
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
                    <div className="flex justify-between items-center">
                      <span className={`text-xs px-2 py-1 rounded-full ${item.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>{item.is_published ? 'Birt' : 'Drög'}</span>
                      <div className="flex space-x-2">
                        <button onClick={() => openEditModal('recipe', item)} className="text-blue-600">✏️</button>
                        <button onClick={() => deleteItem('recipe', item.id)} className="text-red-600">🗑️</button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risks Tab */}
        {activeTab === 'risks' && (
          <div className="space-y-6">
            {/* Risk Section Settings */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Stillingar Áhættuþátta hluta</h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Titill á forsíðu</label>
                  <input type="text" value={riskSectionConfig.title} onChange={(e) => setRiskSectionConfig({...riskSectionConfig, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" placeholder="9 Áhættuþættir" />
                </div>
                <div className="flex items-center">
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" checked={riskSectionConfig.useGradient} onChange={(e) => setRiskSectionConfig({...riskSectionConfig, useGradient: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-3" />
                    <span className="font-medium">Nota rautt-til-bleikt litafall á titil (eins og Hero)</span>
                  </label>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">Undirtitill</label>
                <input type="text" value={riskSectionConfig.subtitle} onChange={(e) => setRiskSectionConfig({...riskSectionConfig, subtitle: e.target.value})} className="w-full px-4 py-3 border rounded-lg" placeholder="Þekktu áhættuþættina og vernda hjarta þitt" />
              </div>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium mb-2">Forskoðun:</h4>
                <span className={`text-3xl font-bold ${riskSectionConfig.useGradient ? 'bg-gradient-to-r from-red-600 to-pink-500 bg-clip-text text-transparent' : 'text-gray-900'}`}>{riskSectionConfig.title}</span>
                <p className="text-gray-600 mt-2">{riskSectionConfig.subtitle}</p>
              </div>
              <button onClick={async () => { await saveConfigToDb('riskSectionConfig', riskSectionConfig); showToast('Stillingar vistaðar!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista stillingar</button>
            </div>

            {/* Risk Items */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Áhættuþættir ({risks.length})</h2>
                <button onClick={() => openEditModal('risk')} className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700">+ Nýr þáttur</button>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {risks.map(item => (
                  <div key={item.id} className="bg-gray-50 rounded-xl shadow p-4 border-l-4 border-red-600">
                    <div className="flex items-center mb-2">
                      <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3"><i className={`${item.icon} text-red-600`}></i></div>
                      <h3 className="font-bold text-gray-900">{item.title}</h3>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">{item.description}</p>
                    <div className="flex justify-end space-x-2">
                      <button onClick={() => openEditModal('risk', item)} className="text-blue-600 hover:text-blue-800"><i className="fas fa-edit"></i></button>
                      <button onClick={() => deleteItem('risk', item.id)} className="text-red-600 hover:text-red-800"><i className="fas fa-trash"></i></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* About Us Tab */}
        {activeTab === 'aboutus' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Um okkur síða</h2>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Titill í valmynd</label>
                    <input type="text" value={aboutUsPageConfig.menuTitle} onChange={(e) => setAboutUsPageConfig({...aboutUsPageConfig, menuTitle: e.target.value})} className="w-full px-4 py-3 border rounded-lg" placeholder="GoRed Ísland" />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input type="checkbox" checked={aboutUsPageConfig.enabled} onChange={(e) => setAboutUsPageConfig({...aboutUsPageConfig, enabled: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-3" />
                      <span className="font-medium">Sýna í valmynd</span>
                    </label>
                  </div>
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Efni síðu</label>
                  <RichTextEditor value={aboutUsPageConfig.content} onChange={(val) => setAboutUsPageConfig({...aboutUsPageConfig, content: val})} />
                </div>
                <button onClick={async () => { await saveConfigToDb('aboutUsPageConfig', aboutUsPageConfig); showToast('Um okkur síða vistuð!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista síðu</button>
              </div>
            </div>
          </div>
        )}

        {/* Menu Tab */}
        {activeTab === 'menu' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📋 Valmyndastjórnun</h2>
              <p className="text-gray-600 mb-4">Stjórnaðu hvaða atriði birtast í haus vefsíðunnar. Merktu við til að sýna/fela.</p>
              <div className="space-y-3 mb-8">
                {menuItems.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <input type="checkbox" checked={item.visible} onChange={() => toggleMenuVisibility(item.id)} className="w-5 h-5 text-red-600 rounded" />
                      {editingMenuItem === item.id ? (
                        <input type="text" value={item.label} onChange={(e) => updateMenuItemLabel(item.id, e.target.value)} onBlur={() => setEditingMenuItem(null)} onKeyDown={(e) => e.key === 'Enter' && setEditingMenuItem(null)} autoFocus className="px-2 py-1 border rounded" />
                      ) : (
                        <span className="font-medium cursor-pointer" onClick={() => setEditingMenuItem(item.id)}>{item.label}</span>
                      )}
                      <span className="text-gray-500 text-sm">({item.path})</span>
                      {item.system && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Kerfi</span>}
                    </div>
                    <div className="flex items-center space-x-2">
                      {!item.system && item.hasPage && (
                        <button onClick={() => setEditingPage(customPages.find(p => p.id === item.id))} className="text-green-600 text-sm px-2 py-1 bg-green-50 rounded">📝 Breyta síðu</button>
                      )}
                      <button onClick={() => setEditingMenuItem(item.id)} className="text-blue-600 text-sm">✏️</button>
                      <span className={`text-xs px-2 py-1 rounded-full ${item.visible ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{item.visible ? 'Sýnilegt' : 'Falið'}</span>
                      {!item.system && <button onClick={() => deleteMenuItem(item.id)} className="text-red-600">🗑️</button>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-6">
                <h3 className="font-bold text-gray-700 mb-4">Bæta við nýrri síðu:</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <input type="text" value={newMenuItem.label} onChange={(e) => setNewMenuItem({...newMenuItem, label: e.target.value})} placeholder="Nafn (t.d. Blogg)" className="px-4 py-2 border rounded-lg" />
                  <input type="text" value={newMenuItem.path} onChange={(e) => setNewMenuItem({...newMenuItem, path: e.target.value})} placeholder="Slóð (t.d. /blogg)" className="px-4 py-2 border rounded-lg" />
                  <button onClick={addMenuItem} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">➕ Bæta við síðu</button>
                </div>
              </div>
              <div className="mt-6"><button onClick={saveMenuItems} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">💾 Vista valmynd</button></div>
            </div>
            
            {/* Custom Page Editor Modal */}
            {editingPage && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                    <h3 className="text-xl font-bold">Breyta síðu: {editingPage.title}</h3>
                    <button onClick={() => setEditingPage(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Titill síðu</label>
                      <input type="text" value={editingPage.title} onChange={(e) => setEditingPage({...editingPage, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Innihald (HTML studdar)</label>
                      <RichTextEditor value={editingPage.content} onChange={(val) => setEditingPage({...editingPage, content: val})} />
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={() => saveCustomPage(editingPage)} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">💾 Vista síðu</button>
                      <button onClick={() => setEditingPage(null)} className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-400">Hætta við</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📷 Myndasöfn</h2>
              <p className="text-gray-600 mb-4">Búðu til myndasöfn sem birtast á „Myndir" síðunni. Hægt er að fela eða sýna einstök söfn.</p>
              
              {/* Create new gallery */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-gray-700 mb-4">Búa til nýtt myndasafn</h3>
                <div className="space-y-4">
                  <input type="text" value={newGallery.name} onChange={(e) => setNewGallery({...newGallery, name: e.target.value})} placeholder="Nafn myndasafns" className="w-full px-4 py-3 border rounded-lg" />
                  <textarea value={newGallery.description} onChange={(e) => setNewGallery({...newGallery, description: e.target.value})} placeholder="Lýsing (valkvætt)" className="w-full px-4 py-3 border rounded-lg" rows={2} />
                  <button onClick={addGallery} className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700">➕ Búa til myndasafn</button>
                </div>
              </div>
              
              {/* Gallery list */}
              <div className="space-y-4">
                {galleries.map(gallery => (
                  <div key={gallery.id} className={`border rounded-lg p-4 ${gallery.visible !== false ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-gray-50 opacity-75'}`}>
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center space-x-3">
                        <input 
                          type="checkbox" 
                          checked={gallery.visible !== false} 
                          onChange={async () => {
                            const updated = galleries.map(g => g.id === gallery.id ? {...g, visible: !g.visible} : g);
                            setGalleries(updated);
                            await saveConfigToDb('galleries', updated);
                            showToast(gallery.visible !== false ? 'Myndasafn falið!' : 'Myndasafn sýnilegt!');
                          }} 
                          className="w-5 h-5 text-red-600 rounded" 
                        />
                        <div>
                          <h3 className="font-bold text-lg">{gallery.name}</h3>
                          {gallery.description && <p className="text-gray-600 text-sm">{gallery.description.replace(/<[^>]*>/g, '').substring(0, 50)}</p>}
                          <p className="text-gray-500 text-xs mt-1">{(gallery.images || []).length} myndir · <span className={gallery.visible !== false ? 'text-green-600' : 'text-gray-500'}>{gallery.visible !== false ? 'Sýnilegt' : 'Falið'}</span></p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => setEditingGallery(gallery)} className="text-blue-600 text-sm px-3 py-1 bg-blue-50 rounded">✏️ Breyta</button>
                        <button onClick={() => deleteGallery(gallery.id)} className="text-red-600 text-sm px-3 py-1 bg-red-50 rounded">🗑️ Eyða</button>
                      </div>
                    </div>
                    {/* Thumbnail preview */}
                    {(gallery.images || []).length > 0 && (
                      <div className="flex gap-2 overflow-x-auto">
                        {(gallery.images || []).slice(0, 5).map(img => (
                          <img key={img.id} src={img.url} alt="" className="w-20 h-20 object-cover rounded" />
                        ))}
                        {(gallery.images || []).length > 5 && <div className="w-20 h-20 bg-gray-200 rounded flex items-center justify-center text-gray-600">+{gallery.images.length - 5}</div>}
                      </div>
                    )}
                  </div>
                ))}
                {galleries.length === 0 && <p className="text-gray-500 text-center py-8">Engin myndasöfn ennþá. Búðu til þitt fyrsta myndasafn!</p>}
              </div>
            </div>
            
            {/* Gallery Editor Modal */}
            {editingGallery && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                    <h3 className="text-xl font-bold">Breyta: {editingGallery.name}</h3>
                    <button onClick={() => setEditingGallery(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Nafn</label>
                      <input type="text" value={editingGallery.name} onChange={(e) => setEditingGallery({...editingGallery, name: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                      <RichTextEditor value={editingGallery.description || ''} onChange={(val) => setEditingGallery({...editingGallery, description: val})} />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Myndir ({(editingGallery.images || []).length})</label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                        {(editingGallery.images || []).map(img => (
                          <div key={img.id} className="relative group">
                            <img src={img.url} alt="" className="w-full h-24 object-cover rounded" />
                            <button onClick={() => {
                              setEditingGallery({...editingGallery, images: editingGallery.images.filter(i => i.id !== img.id)});
                            }} className="absolute top-1 right-1 bg-red-600 text-white w-6 h-6 rounded-full opacity-0 group-hover:opacity-100 transition">×</button>
                          </div>
                        ))}
                      </div>
                      <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                        <i className="fas fa-upload mr-2"></i>Hlaða upp myndum (margar í einu)
                        <input type="file" accept="image/*" multiple className="hidden" onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length > 0) {
                            const newImages = [];
                            for (const file of files) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                                const data = await res.json();
                                if (data.url) {
                                  newImages.push({ id: Date.now() + Math.random(), url: data.url });
                                }
                              } catch (err) { console.error('Upload error:', err); }
                            }
                            setEditingGallery({...editingGallery, images: [...(editingGallery.images || []), ...newImages]});
                          }
                        }} />
                      </label>
                    </div>
                    <div className="flex items-center mt-4">
                      <input type="checkbox" checked={editingGallery.show_author !== false} onChange={(e) => setEditingGallery({...editingGallery, show_author: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-2" />
                      <span className="font-medium">Sýna höfund</span>
                      {editingGallery.created_by && <span className="text-sm text-gray-500 ml-2">(Höfundur: {editingGallery.created_by})</span>}
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={() => updateGallery(editingGallery)} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista myndasafn</button>
                      <button onClick={() => setEditingGallery(null)} className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-400">Hætta við</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Events Tab */}
        {activeTab === 'events' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Viðburðir</h2>
                <div className="flex items-center space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      checked={eventsConfig.enabled} 
                      onChange={async (e) => {
                        const updated = {...eventsConfig, enabled: e.target.checked};
                        setEventsConfig(updated);
                        await saveConfigToDb('eventsConfig', updated);
                        showToast(e.target.checked ? 'Viðburðir sýnilegir!' : 'Viðburðir faldir!');
                      }} 
                      className="w-5 h-5 text-red-600 rounded mr-2" 
                    />
                    <span className="font-medium">Sýna í valmynd</span>
                  </label>
                </div>
              </div>
              <p className="text-gray-600 mb-6">Búðu til viðburði með dagatali og korti sem sýnir staðsetningar.</p>
              
              {/* Create new event */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 mb-6">
                <h3 className="font-bold text-gray-700 mb-4">Búa til nýjan viðburð</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Titill *</label>
                      <input type="text" value={newEvent.title} onChange={(e) => setNewEvent(prev => ({...prev, title: e.target.value}))} placeholder="Nafn viðburðar" className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Staðsetning</label>
                      <input type="text" value={newEvent.location_name} onChange={(e) => setNewEvent(prev => ({...prev, location_name: e.target.value}))} placeholder="T.d. Harpa, Reykjavík" className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Dagsetning *</label>
                      <input type="date" value={newEvent.date} onChange={(e) => setNewEvent(prev => ({...prev, date: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Tími (24 klst snið)</label>
                      <input 
                        type="text" 
                        value={newEvent.time} 
                        onChange={(e) => setNewEvent(prev => ({...prev, time: e.target.value}))} 
                        placeholder="t.d. 14:30" 
                        pattern="[0-2][0-9]:[0-5][0-9]"
                        className="w-full px-4 py-3 border rounded-lg" 
                      />
                      <p className="text-xs text-gray-400 mt-1">Sláðu inn tíma á forminu 00:00 til 23:59</p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Heimilisfang / Staðsetning</label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={newEvent.location_name || ''} 
                        onChange={(e) => setNewEvent(prev => ({...prev, location_name: e.target.value}))} 
                        placeholder="t.d. Laugavegur 1, Reykjavík" 
                        className="flex-1 px-4 py-3 border rounded-lg" 
                      />
                      <button 
                        type="button"
                        onClick={async () => {
                          if (newEvent.location_name) {
                            try {
                              const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(newEvent.location_name + ', Iceland')}&limit=1`);
                              const data = await res.json();
                              if (data && data[0]) {
                                setNewEvent(prev => ({
                                  ...prev, 
                                  latitude: parseFloat(data[0].lat), 
                                  longitude: parseFloat(data[0].lon)
                                }));
                                showToast('Staðsetning fundin!');
                              } else {
                                showToast('Staðsetning fannst ekki. Reyndu aftur.');
                              }
                            } catch (err) { 
                              console.error('Geocoding error:', err);
                              showToast('Villa við leit að staðsetningu');
                            }
                          }
                        }}
                        className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700"
                        title="Leita að staðsetningu"
                      >
                        <i className="fas fa-search mr-2"></i>Leita
                      </button>
                    </div>
                    {newEvent.latitude && newEvent.longitude && newEvent.latitude !== 64.1466 && (
                      <p className="text-xs text-green-600 mt-1"><i className="fas fa-check-circle mr-1"></i>Staðsetning fundin: {newEvent.latitude.toFixed(4)}, {newEvent.longitude.toFixed(4)}</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">Sláðu inn heimilisfang og smelltu á "Leita" til að finna staðsetningu á korti</p>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                    <textarea value={newEvent.description} onChange={(e) => setNewEvent(prev => ({...prev, description: e.target.value}))} placeholder="Lýsing á viðburði..." className="w-full px-4 py-3 border rounded-lg" rows={4} />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Vefsíða</label>
                    <input type="url" value={newEvent.website || ''} onChange={(e) => setNewEvent(prev => ({...prev, website: e.target.value}))} placeholder="https://..." className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Mynd</label>
                    <div className="flex gap-4 items-start">
                      <input type="url" value={newEvent.image_url} onChange={(e) => setNewEvent(prev => ({...prev, image_url: e.target.value}))} placeholder="Sláðu inn URL eða hladdu upp" className="flex-1 px-4 py-3 border rounded-lg" />
                      <label className="inline-flex items-center px-4 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                        <i className="fas fa-upload mr-2"></i> Hlaða upp
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('image', file);
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                              const data = await res.json();
                              if (data.url) setNewEvent(prev => ({...prev, image_url: data.url}));
                            } catch (err) { console.error('Upload error:', err); }
                          }
                        }} />
                      </label>
                    </div>
                    {newEvent.image_url && <img src={newEvent.image_url} alt="Forskoðun" className="mt-2 h-24 object-cover rounded-lg" />}
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={newEvent.show_author} onChange={(e) => setNewEvent(prev => ({...prev, show_author: e.target.checked}))} className="w-5 h-5 text-red-600 rounded mr-2" />
                    <span className="font-medium">Sýna höfund</span>
                  </div>
                  <button 
                    onClick={async () => {
                      console.log('Creating event with:', newEvent);
                      if (newEvent.title && newEvent.date) {
                        const eventData = { 
                          ...newEvent, 
                          id: Date.now(), 
                          created_by: loggedInUser?.username || 'admin',
                          created_at: new Date().toISOString()
                        };
                        const updated = [...events, eventData];
                        setEvents(updated);
                        await saveConfigToDb('events', updated);
                        setNewEvent({ title: '', date: '', time: '', description: '', image_url: '', location_name: '', latitude: 64.1466, longitude: -21.9426, website: '', show_author: true, created_by: '' });
                        showToast('Viðburður búinn til!');
                      } else {
                        showToast('Vinsamlegast fylltu út titil og dagsetningu');
                      }
                    }} 
                    className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700"
                  >
                    Búa til viðburð
                  </button>
                </div>
              </div>
              
              {/* Events list */}
              <div className="space-y-4">
                <h3 className="font-bold text-gray-700">Viðburðir ({events.length})</h3>
                {events.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">Engir viðburðir. Búðu til fyrsta viðburðinn!</p>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    {events.map(event => (
                      <div key={event.id} className="border rounded-lg p-4 bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h4 className="font-bold text-lg">{event.title}</h4>
                            <p className="text-gray-600 text-sm">{event.date} {event.time && `kl. ${event.time}`}</p>
                            <p className="text-gray-500 text-sm">{event.location_name}</p>
                            {event.show_author && event.created_by && <p className="text-xs text-gray-400 mt-1">Höfundur: {event.created_by}</p>}
                          </div>
                          {event.image_url && <img src={event.image_url} alt="" className="w-16 h-16 object-cover rounded-lg ml-4" />}
                        </div>
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setEditingEvent(event)} className="text-blue-600 text-sm px-3 py-1 bg-blue-50 rounded">Breyta</button>
                          <button onClick={async () => {
                            if (window.confirm('Eyða viðburði?')) {
                              const updated = events.filter(e => e.id !== event.id);
                              setEvents(updated);
                              await saveConfigToDb('events', updated);
                              showToast('Viðburði eytt!');
                            }
                          }} className="text-red-600 text-sm px-3 py-1 bg-red-50 rounded">Eyða</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Event Calendar Preview */}
              {events.length > 0 && (
                <div className="mt-8 border-t pt-6">
                  <h3 className="font-bold text-gray-700 mb-4">Dagatal - Yfirlit</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="space-y-2">
                      {events.sort((a, b) => new Date(a.date) - new Date(b.date)).map(event => (
                        <div key={event.id} className="flex items-center p-2 bg-white rounded border-l-4 border-red-500">
                          <div className="w-16 text-center mr-4">
                            <div className="text-2xl font-bold text-red-600">{new Date(event.date).getDate()}</div>
                            <div className="text-xs text-gray-500">{new Date(event.date).toLocaleDateString('is-IS', { month: 'short' })}</div>
                          </div>
                          <div className="flex-1">
                            <div className="font-medium">{event.title}</div>
                            <div className="text-sm text-gray-500">{event.time && `${event.time} - `}{event.location_name}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Edit Event Modal */}
            {editingEvent && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                    <h3 className="text-xl font-bold">Breyta viðburði</h3>
                    <button onClick={() => setEditingEvent(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Titill</label>
                        <input type="text" value={editingEvent.title} onChange={(e) => setEditingEvent(prev => ({...prev, title: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Dagsetning</label>
                        <input type="date" value={editingEvent.date} onChange={(e) => setEditingEvent(prev => ({...prev, date: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" />
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Tími (24 klst snið)</label>
                        <input 
                          type="text" 
                          value={editingEvent.time || ''} 
                          onChange={(e) => setEditingEvent(prev => ({...prev, time: e.target.value}))} 
                          placeholder="t.d. 14:30" 
                          pattern="[0-2][0-9]:[0-5][0-9]"
                          className="w-full px-4 py-3 border rounded-lg" 
                        />
                        <p className="text-xs text-gray-400 mt-1">Sláðu inn tíma á forminu 00:00 til 23:59</p>
                      </div>
                      <div>
                        <label className="block text-gray-700 font-medium mb-2">Vefsíða</label>
                        <input type="url" value={editingEvent.website || ''} onChange={(e) => setEditingEvent(prev => ({...prev, website: e.target.value}))} placeholder="https://..." className="w-full px-4 py-3 border rounded-lg" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Heimilisfang / Staðsetning</label>
                      <div className="flex gap-2">
                        <input 
                          type="text" 
                          value={editingEvent.location_name || ''} 
                          onChange={(e) => setEditingEvent(prev => ({...prev, location_name: e.target.value}))} 
                          placeholder="t.d. Laugavegur 1, Reykjavík" 
                          className="flex-1 px-4 py-3 border rounded-lg" 
                        />
                        <button 
                          type="button"
                          onClick={async () => {
                            if (editingEvent.location_name) {
                              try {
                                const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(editingEvent.location_name + ', Iceland')}&limit=1`);
                                const data = await res.json();
                                if (data && data[0]) {
                                  setEditingEvent(prev => ({
                                    ...prev, 
                                    latitude: parseFloat(data[0].lat), 
                                    longitude: parseFloat(data[0].lon)
                                  }));
                                  showToast('Staðsetning fundin!');
                                } else {
                                  showToast('Staðsetning fannst ekki. Reyndu aftur.');
                                }
                              } catch (err) { 
                                console.error('Geocoding error:', err);
                                showToast('Villa við leit að staðsetningu');
                              }
                            }
                          }}
                          className="bg-indigo-600 text-white px-4 py-3 rounded-lg hover:bg-indigo-700"
                          title="Leita að staðsetningu"
                        >
                          <i className="fas fa-search mr-2"></i>Leita
                        </button>
                      </div>
                      {editingEvent.latitude && editingEvent.longitude && editingEvent.latitude !== 64.1466 && (
                        <p className="text-xs text-green-600 mt-1"><i className="fas fa-check-circle mr-1"></i>Staðsetning fundin: {editingEvent.latitude.toFixed(4)}, {editingEvent.longitude.toFixed(4)}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">Sláðu inn heimilisfang og smelltu á "Leita" til að finna staðsetningu á korti</p>
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                      <textarea value={editingEvent.description || ''} onChange={(e) => setEditingEvent(prev => ({...prev, description: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" rows={4} />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mynd</label>
                      <div className="flex gap-4 items-start">
                        <input type="url" value={editingEvent.image_url || ''} onChange={(e) => setEditingEvent(prev => ({...prev, image_url: e.target.value}))} className="flex-1 px-4 py-3 border rounded-lg" placeholder="Sláðu inn URL eða hladdu upp" />
                        <label className="inline-flex items-center px-4 py-3 bg-blue-600 text-white rounded-lg cursor-pointer hover:bg-blue-700">
                          <i className="fas fa-upload mr-2"></i> Hlaða upp
                          <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const formData = new FormData();
                              formData.append('image', file);
                              try {
                                const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                                const data = await res.json();
                                if (data.url) setEditingEvent(prev => ({...prev, image_url: data.url}));
                              } catch (err) { console.error('Upload error:', err); }
                            }
                          }} />
                        </label>
                      </div>
                      {editingEvent.image_url && <img src={editingEvent.image_url} alt="" className="mt-2 h-24 object-cover rounded-lg" />}
                    </div>
                    <div className="flex items-center">
                      <input type="checkbox" checked={editingEvent.show_author !== false} onChange={(e) => setEditingEvent(prev => ({...prev, show_author: e.target.checked}))} className="w-5 h-5 text-red-600 rounded mr-2" />
                      <span className="font-medium">Sýna höfund</span>
                    </div>
                    <div className="flex gap-4 pt-4">
                      <button onClick={async () => {
                        const updated = events.map(e => e.id === editingEvent.id ? editingEvent : e);
                        setEvents(updated);
                        await saveConfigToDb('events', updated);
                        setEditingEvent(null);
                        showToast('Viðburður uppfærður!');
                      }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista</button>
                      <button onClick={() => setEditingEvent(null)} className="bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-400">Hætta við</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Blog Tab */}
        {activeTab === 'blog' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900"><i className="fas fa-blog mr-2"></i>Blog</h2>
                <div className="flex items-center gap-4">
                  <input type="text" value={blogConfig.menuTitle} onChange={(e) => setBlogConfig({...blogConfig, menuTitle: e.target.value})} placeholder="Nafn í valmynd" className="px-3 py-2 border rounded-lg text-sm" />
                  <label className="flex items-center cursor-pointer">
                    <input type="checkbox" checked={blogConfig.enabled} onChange={(e) => setBlogConfig({...blogConfig, enabled: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-2" />
                    <span className="text-sm">Sýna í valmynd</span>
                  </label>
                  <button onClick={async () => { 
                    await saveConfigToDb('blogConfig', blogConfig); 
                    const updatedMenu = menuItems.map(m => m.path === '/blog' ? {...m, label: blogConfig.menuTitle, visible: blogConfig.enabled} : m);
                    setMenuItems(updatedMenu);
                    await saveConfigToDb('menuItems', updatedMenu);
                    showToast('Stillingar vistaðar!'); 
                  }} className="bg-indigo-600 text-white px-3 py-2 rounded-lg text-sm">Vista</button>
                </div>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="font-bold text-gray-900 mb-4">Ný færsla</h3>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Titill *</label>
                    <input type="text" value={newBlogPost.title} onChange={(e) => setNewBlogPost(prev => ({...prev, title: e.target.value}))} placeholder="Titill færslu" className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Tengill (valfrjálst)</label>
                    <input type="url" value={newBlogPost.link_url} onChange={(e) => setNewBlogPost(prev => ({...prev, link_url: e.target.value}))} placeholder="https://..." className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-gray-700 font-medium mb-2">Efni</label>
                  <RichTextEditor value={newBlogPost.content} onChange={(val) => setNewBlogPost(prev => ({...prev, content: val}))} />
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Mynd</label>
                    <div className="flex gap-2">
                      <input type="url" value={newBlogPost.image_url} onChange={(e) => setNewBlogPost(prev => ({...prev, image_url: e.target.value}))} placeholder="URL á mynd" className="flex-1 px-4 py-3 border rounded-lg" />
                      <label className="bg-blue-600 text-white px-4 py-3 rounded-lg cursor-pointer hover:bg-blue-700">
                        <i className="fas fa-upload"></i>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('image', file);
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                              const data = await res.json();
                              if (data.url) setNewBlogPost(prev => ({...prev, image_url: data.url}));
                            } catch (err) { console.error(err); }
                          }
                        }} />
                      </label>
                    </div>
                    {newBlogPost.image_url && <img src={newBlogPost.image_url} alt="" className="mt-2 h-20 rounded" />}
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={newBlogPost.show_author} onChange={(e) => setNewBlogPost(prev => ({...prev, show_author: e.target.checked}))} className="w-5 h-5 text-red-600 rounded mr-2" />
                    <span className="font-medium">Sýna höfund</span>
                  </div>
                </div>
                <button onClick={async () => {
                  if (!newBlogPost.title) return showToast('Vinsamlegast sláðu inn titil');
                  const post = { ...newBlogPost, id: Date.now(), created_by: loggedInUser?.username || 'admin', created_at: new Date().toISOString() };
                  const updated = [...blogPosts, post];
                  setBlogPosts(updated);
                  await saveConfigToDb('blogPosts', updated);
                  setNewBlogPost({ title: '', content: '', image_url: '', link_url: '', show_author: true, created_by: '' });
                  showToast('Blogg bætt við!');
                }} className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700">
                  <i className="fas fa-plus mr-2"></i>Bæta við
                </button>
              </div>
              
              <h3 className="font-bold text-gray-900 mb-4">Bloggfærslur ({blogPosts.length})</h3>
              <div className="space-y-4">
                {blogPosts.map(post => (
                  <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center gap-4">
                      {post.image_url && <img src={post.image_url} alt="" className="w-16 h-16 object-cover rounded" />}
                      <div>
                        <h4 className="font-bold">{post.title}</h4>
                        {post.link_url && <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm"><i className="fas fa-external-link-alt mr-1"></i>Tengill</a>}
                        <p className="text-gray-500 text-sm">Höfundur: {post.created_by}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditingBlogPost(post)} className="text-blue-600 hover:text-blue-800"><i className="fas fa-edit"></i></button>
                      <button onClick={async () => {
                        const updated = blogPosts.filter(p => p.id !== post.id);
                        setBlogPosts(updated);
                        await saveConfigToDb('blogPosts', updated);
                        showToast('Eytt!');
                      }} className="text-red-600 hover:text-red-800"><i className="fas fa-trash"></i></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Edit Blog Modal */}
            {editingBlogPost && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                  <div className="p-6 border-b flex justify-between items-center">
                    <h3 className="text-xl font-bold">Breyta bloggfærslu</h3>
                    <button onClick={() => setEditingBlogPost(null)} className="text-gray-500 hover:text-gray-700">✕</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Titill</label>
                      <input type="text" value={editingBlogPost.title} onChange={(e) => setEditingBlogPost({...editingBlogPost, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Tengill</label>
                      <input type="url" value={editingBlogPost.link_url || ''} onChange={(e) => setEditingBlogPost({...editingBlogPost, link_url: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Efni</label>
                      <textarea value={editingBlogPost.content} onChange={(e) => setEditingBlogPost({...editingBlogPost, content: e.target.value})} className="w-full px-4 py-3 border rounded-lg" rows={4} />
                    </div>
                    <div>
                      <label className="block text-gray-700 font-medium mb-2">Mynd</label>
                      <input type="url" value={editingBlogPost.image_url || ''} onChange={(e) => setEditingBlogPost({...editingBlogPost, image_url: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                    </div>
                    <button onClick={async () => {
                      const updated = blogPosts.map(p => p.id === editingBlogPost.id ? editingBlogPost : p);
                      setBlogPosts(updated);
                      await saveConfigToDb('blogPosts', updated);
                      setEditingBlogPost(null);
                      showToast('Bloggfærsla uppfærð!');
                    }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sponsors Tab */}
        {activeTab === 'sponsors' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900"><i className="fas fa-handshake mr-2"></i>Bakjarar / Styrktaraðilar</h2>
                <label className="flex items-center cursor-pointer">
                  <input type="checkbox" checked={sponsorsConfig.enabled} onChange={(e) => setSponsorsConfig({...sponsorsConfig, enabled: e.target.checked})} className="w-5 h-5 text-red-600 rounded mr-2" />
                  <span className="text-sm">Sýna á forsíðu</span>
                </label>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Titill</label>
                  <input type="text" value={sponsorsConfig.title} onChange={(e) => setSponsorsConfig({...sponsorsConfig, title: e.target.value})} placeholder="Bakhjarlar okkar" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Undirtitill</label>
                  <input type="text" value={sponsorsConfig.subtitle} onChange={(e) => setSponsorsConfig({...sponsorsConfig, subtitle: e.target.value})} placeholder="Þökk sé þessum aðilum" className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <button onClick={async () => { await saveConfigToDb('sponsorsConfig', sponsorsConfig); showToast('Stillingar vistaðar!'); }} className="bg-indigo-600 text-white px-4 py-2 rounded-lg mb-6">Vista stillingar</button>
              
              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="font-bold text-gray-900 mb-4">Bæta við bakjara</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Nafn</label>
                    <input type="text" value={newSponsor.name} onChange={(e) => setNewSponsor({...newSponsor, name: e.target.value})} placeholder="Nafn fyrirtækis" className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Lógó</label>
                    <div className="flex gap-2">
                      <input type="url" value={newSponsor.logo_url} onChange={(e) => setNewSponsor({...newSponsor, logo_url: e.target.value})} placeholder="URL á lógó" className="flex-1 px-4 py-3 border rounded-lg" />
                      <label className="bg-blue-600 text-white px-4 py-3 rounded-lg cursor-pointer hover:bg-blue-700">
                        <i className="fas fa-upload"></i>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const formData = new FormData();
                            formData.append('image', file);
                            try {
                              const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                              const data = await res.json();
                              if (data.url) setNewSponsor({...newSponsor, logo_url: data.url});
                            } catch (err) { console.error(err); }
                          }
                        }} />
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Vefsíða (valfrjálst)</label>
                    <input type="url" value={newSponsor.website} onChange={(e) => setNewSponsor({...newSponsor, website: e.target.value})} placeholder="https://..." className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                </div>
                {newSponsor.logo_url && <img src={newSponsor.logo_url} alt="" className="mt-3 h-16 object-contain" />}
                <button onClick={async () => {
                  if (!newSponsor.name || !newSponsor.logo_url) return showToast('Vinsamlegast fylltu út nafn og lógó');
                  const sponsor = { ...newSponsor, id: Date.now() };
                  const updated = [...sponsors, sponsor];
                  setSponsors(updated);
                  await saveConfigToDb('sponsors', updated);
                  setNewSponsor({ name: '', logo_url: '', website: '' });
                  showToast('Bakjari bættur við!');
                }} className="mt-4 bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700">
                  <i className="fas fa-plus mr-2"></i>Bæta við
                </button>
              </div>
              
              <h3 className="font-bold text-gray-900 mb-4">Bakhjarlar ({sponsors.length})</h3>
              <div className="grid md:grid-cols-4 gap-4">
                {sponsors.map(sponsor => (
                  <div key={sponsor.id} className="border rounded-lg p-4 text-center bg-gray-50 relative group">
                    <button onClick={async () => {
                      const updated = sponsors.filter(s => s.id !== sponsor.id);
                      setSponsors(updated);
                      await saveConfigToDb('sponsors', updated);
                      showToast('Eytt!');
                    }} className="absolute top-2 right-2 text-red-600 opacity-0 group-hover:opacity-100 transition">
                      <i className="fas fa-times"></i>
                    </button>
                    {sponsor.logo_url && <img src={sponsor.logo_url} alt={sponsor.name} className="h-16 mx-auto object-contain mb-2" />}
                    <p className="font-medium text-sm">{sponsor.name}</p>
                    {sponsor.website && <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-xs"><i className="fas fa-external-link-alt"></i></a>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Footer Tab */}
        {activeTab === 'footer' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Stillingar</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div><label className="block text-gray-700 font-medium mb-2">Nafn fyrirtækis</label>
                  <input type="text" value={footerData.companyName || ''} onChange={(e) => setFooterData({...footerData, companyName: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block text-gray-700 font-medium mb-2">Kjörorð</label>
                  <input type="text" value={footerData.tagline || ''} onChange={(e) => setFooterData({...footerData, tagline: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block text-gray-700 font-medium mb-2">Netfang</label>
                  <input type="email" value={footerData.email || ''} onChange={(e) => setFooterData({...footerData, email: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block text-gray-700 font-medium mb-2">Sími</label>
                  <input type="text" value={footerData.phone || ''} onChange={(e) => setFooterData({...footerData, phone: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div className="md:col-span-2"><label className="block text-gray-700 font-medium mb-2">Höfundarréttur</label>
                  <input type="text" value={footerData.copyright || ''} onChange={(e) => setFooterData({...footerData, copyright: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Nöfn á fótarhlutum</h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div><label className="block text-gray-700 font-medium mb-2">Tenglar titill</label>
                  <input type="text" value={footerData.linksTitle || 'Flýtitenglar'} onChange={(e) => setFooterData({...footerData, linksTitle: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block text-gray-700 font-medium mb-2">Samband titill</label>
                  <input type="text" value={footerData.contactTitle || 'Hafa samband'} onChange={(e) => setFooterData({...footerData, contactTitle: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
                <div><label className="block text-gray-700 font-medium mb-2">Samfélagsmiðlar titill</label>
                  <input type="text" value={footerData.socialTitle || 'Fylgstu með'} onChange={(e) => setFooterData({...footerData, socialTitle: e.target.value})} className="w-full px-4 py-3 border rounded-lg" /></div>
              </div>
            </div>

            {/* Contact Info Section */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Tengiliðaupplýsingar (undir Hafa samband)</h2>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2"><i className="fas fa-envelope mr-2 text-red-500"></i>Netfang</label>
                  <input type="email" value={contactInfo.email} onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})} placeholder="info@gored.is" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2"><i className="fas fa-phone mr-2 text-red-500"></i>Símanúmer</label>
                  <input type="tel" value={contactInfo.phone} onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})} placeholder="+354 123 4567" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2"><i className="fas fa-map-marker-alt mr-2 text-red-500"></i>Heimilisfang</label>
                  <input type="text" value={contactInfo.address} onChange={(e) => setContactInfo({...contactInfo, address: e.target.value})} placeholder="Laugavegur 1, 101 Reykjavík" className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <button onClick={async () => { await saveConfigToDb('contactInfo', contactInfo); showToast('Tengiliðaupplýsingar vistaðar!'); }} className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700">Vista tengiliðaupplýsingar</button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Samfélagsmiðlar</h2>
              <div className="space-y-3 mb-6">
                {socialLinks.map(link => (
                  <div key={link.id} className="p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <input type="checkbox" checked={link.visible} onChange={() => setSocialLinks(socialLinks.map(s => s.id === link.id ? {...s, visible: !s.visible} : s))} className="w-5 h-5 text-red-600 rounded" />
                        <div className="w-10 h-10 bg-gray-800 text-white rounded-full flex items-center justify-center"><i className={link.icon}></i></div>
                        <span className={`text-xs px-2 py-1 rounded-full ${link.visible ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{link.visible ? 'Sýnilegt' : 'Falið'}</span>
                      </div>
                      <button onClick={() => deleteSocialLink(link.id)} className="text-red-600 hover:text-red-800">🗑️ Eyða</button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                      <input type="text" value={link.platform} onChange={(e) => setSocialLinks(socialLinks.map(s => s.id === link.id ? {...s, platform: e.target.value} : s))} placeholder="Nafn" className="px-3 py-2 border rounded-lg text-sm" />
                      <select value={link.icon} onChange={(e) => setSocialLinks(socialLinks.map(s => s.id === link.id ? {...s, icon: e.target.value} : s))} className="px-3 py-2 border rounded-lg text-sm">
                        {SOCIAL_ICONS.map(icon => (<option key={icon.value} value={icon.value}>{icon.preview} {icon.label}</option>))}
                      </select>
                      <input type="url" value={link.url} onChange={(e) => setSocialLinks(socialLinks.map(s => s.id === link.id ? {...s, url: e.target.value} : s))} placeholder="URL" className="px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  </div>
                ))}
              </div>
              <div className="border-t pt-6">
                <h3 className="font-bold text-gray-700 mb-4">Bæta við samfélagsmiðli:</h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <input type="text" value={newSocialLink.platform} onChange={(e) => setNewSocialLink({...newSocialLink, platform: e.target.value})} placeholder="Nafn" className="px-4 py-2 border rounded-lg" />
                  <select value={newSocialLink.icon} onChange={(e) => setNewSocialLink({...newSocialLink, icon: e.target.value})} className="px-4 py-2 border rounded-lg">
                    {SOCIAL_ICONS.map(icon => (<option key={icon.value} value={icon.value}>{icon.preview} {icon.label}</option>))}
                  </select>
                  <input type="url" value={newSocialLink.url} onChange={(e) => setNewSocialLink({...newSocialLink, url: e.target.value})} placeholder="URL" className="px-4 py-2 border rounded-lg" />
                  <button onClick={addSocialLink} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">➕ Bæta við</button>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">🔗 Fótartenglar</h2>
              <div className="space-y-3 mb-6">
                {footerLinks.map(link => (
                  <div key={link.id} className="flex items-center justify-between p-4 border rounded-lg bg-gray-50">
                    <div className="flex items-center space-x-4">
                      <input type="checkbox" checked={link.visible} onChange={() => setFooterLinks(footerLinks.map(l => l.id === link.id ? {...l, visible: !l.visible} : l))} className="w-5 h-5 text-red-600 rounded" />
                      <span className="font-medium">{link.label}</span><span className="text-gray-500 text-sm">{link.url}</span>
                    </div>
                    <button onClick={() => deleteFooterLink(link.id)} className="text-red-600">🗑️</button>
                  </div>
                ))}
              </div>
              <div className="border-t pt-6">
                <h3 className="font-bold text-gray-700 mb-4">Bæta við tengli:</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <input type="text" value={newFooterLink.label} onChange={(e) => setNewFooterLink({...newFooterLink, label: e.target.value})} placeholder="Nafn" className="px-4 py-2 border rounded-lg" />
                  <input type="text" value={newFooterLink.url} onChange={(e) => setNewFooterLink({...newFooterLink, url: e.target.value})} placeholder="URL" className="px-4 py-2 border rounded-lg" />
                  <button onClick={addFooterLink} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">➕ Bæta við</button>
                </div>
              </div>
            </div>

            <button onClick={saveFooter} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">💾 Vista fót</button>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">👥 Notendastjórnun</h2>
            <div className="space-y-3 mb-8">
              {adminUsers.map(user => (
                <div key={user.id} className="p-4 border rounded-lg bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-red-600 text-white rounded-full flex items-center justify-center font-bold">{(user.username || user.email || 'U').charAt(0).toUpperCase()}</div>
                      <div>
                        <span className="font-medium">{user.username}</span>
                        {user.email && <span className="text-gray-500 text-sm ml-2">({user.email})</span>}
                        <span className="text-gray-400 text-sm ml-2">Búinn til: {user.created}</span>
                      </div>
                    </div>
                    <button onClick={() => deleteUser(user.id)} className="text-red-600 hover:text-red-800">🗑️ Eyða</button>
                  </div>
                  {/* Password change section */}
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <input 
                        type="password" 
                        id={`newpass-${user.id}`}
                        placeholder="Nýtt lykilorð" 
                        className="flex-1 px-3 py-2 border rounded-lg text-sm"
                      />
                      <button 
                        onClick={async () => {
                          const newPassword = document.getElementById(`newpass-${user.id}`).value;
                          if (newPassword && newPassword.length >= 4) {
                            const updated = adminUsers.map(u => u.id === user.id ? {...u, password: newPassword} : u);
                            setAdminUsers(updated);
                            await saveConfigToDb('adminUsers', updated);
                            document.getElementById(`newpass-${user.id}`).value = '';
                            showToast('Lykilorð uppfært!');
                          } else {
                            showToast('Lykilorð verður að vera að minnsta kosti 4 stafir');
                          }
                        }}
                        className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700"
                      >
                        <i className="fas fa-key mr-1"></i> Breyta lykilorði
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t pt-6">
              <h3 className="font-bold text-gray-700 mb-4">Búa til nýjan notanda:</h3>
              <div className="grid md:grid-cols-4 gap-4">
                <input type="text" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} placeholder="Notandanafn" className="px-4 py-2 border rounded-lg" />
                <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="Netfang" className="px-4 py-2 border rounded-lg" />
                <input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="Lykilorð" className="px-4 py-2 border rounded-lg" />
                <button onClick={addUser} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">➕ Búa til notanda</button>
              </div>
              <p className="text-sm text-gray-500 mt-2">Notandi skráir sig inn með netfangi eða notandanafni og lykilorði.</p>
            </div>
          </div>
        )}

        {/* Donations Tab */}
        {activeTab === 'donations' && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Styrkjaupplýsingar</h2>
            <p className="text-gray-600">Stjórnaðu styrkjamöguleikum. Merktu við til að sýna/fela og smelltu á eyða til að fjarlægja.</p>
            
            {/* All donation options */}
            <div className="space-y-4">
              {donations.map((d, index) => (
                <div key={d.id || index} className={`bg-white rounded-xl shadow-lg p-6 ${d.is_active ? 'border-2 border-green-300' : 'border-2 border-gray-200 opacity-75'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <input 
                        type="checkbox" 
                        checked={d.is_active === true || d.is_active === 1} 
                        onChange={async () => {
                          const updated = donations.map((don, i) => i === index ? {...don, is_active: !don.is_active} : don);
                          setDonations(updated);
                          try {
                            if (d.id) {
                              await fetch(`${BACKEND_URL}/api/donations/${d.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({...d, is_active: !d.is_active})
                              });
                            }
                          } catch (e) { console.log('Update error'); }
                          showToast(d.is_active ? 'Falið!' : 'Sýnilegt!');
                        }} 
                        className="w-5 h-5 text-red-600 rounded" 
                      />
                      <span className="font-bold text-gray-900">{d.donation_type === 'bank' ? 'Bankaupplýsingar' : d.donation_type}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${d.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                        {d.is_active ? 'Sýnilegt' : 'Falið'}
                      </span>
                    </div>
                    <button 
                      onClick={async () => {
                        if (window.confirm('Ertu viss um að eyða þessum styrkjamöguleika?')) {
                          try {
                            if (d.id) {
                              await fetch(`${BACKEND_URL}/api/donations/${d.id}`, { method: 'DELETE' });
                            }
                          } catch (e) { console.log('Delete error'); }
                          setDonations(donations.filter((_, i) => i !== index));
                          showToast('Eytt!');
                        }
                      }}
                      className="text-red-600 hover:text-red-800 px-3 py-1 bg-red-50 rounded"
                    >
                      Eyða
                    </button>
                  </div>
                  <textarea 
                    value={d.info || ''} 
                    onChange={(e) => {
                      const updated = donations.map((don, i) => i === index ? {...don, info: e.target.value} : don);
                      setDonations(updated);
                    }}
                    className="w-full px-4 py-3 border rounded-lg" 
                    rows={4} 
                    placeholder="Upplýsingar..."
                  />
                  <button 
                    onClick={async () => {
                      try {
                        if (d.id) {
                          await fetch(`${BACKEND_URL}/api/donations/${d.id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify(d)
                          });
                        }
                      } catch (e) { console.log('Save error'); }
                      showToast('Vistað!');
                    }}
                    className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                  >
                    Vista breytingar
                  </button>
                </div>
              ))}
              {donations.length === 0 && <p className="text-gray-500 text-center py-8">Engir styrkjamöguleikar. Bættu við neðar.</p>}
            </div>

            {/* Add new donation option */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Bæta við styrkjamöguleika</h3>
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <input type="text" id="new-donation-type" placeholder="Tegund (t.d. bank, paypal)" className="px-4 py-2 border rounded-lg" />
                  <select id="new-donation-active" className="px-4 py-2 border rounded-lg">
                    <option value="1">Sýnilegt</option>
                    <option value="0">Falið</option>
                  </select>
                </div>
                <textarea id="new-donation-info" placeholder="Upplýsingar..." className="w-full px-4 py-3 border rounded-lg" rows={4} />
                <button 
                  onClick={async () => {
                    const dtype = document.getElementById('new-donation-type').value;
                    const dactive = document.getElementById('new-donation-active').value === '1';
                    const dinfo = document.getElementById('new-donation-info').value;
                    if (dtype) {
                      const newDon = { donation_type: dtype, info: dinfo, is_active: dactive };
                      try {
                        const res = await fetch(`${BACKEND_URL}/api/donations`, {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify(newDon)
                        });
                        const result = await res.json();
                        setDonations([...donations, { ...newDon, id: result.id || Date.now() }]);
                      } catch (e) {
                        setDonations([...donations, { ...newDon, id: Date.now() }]);
                      }
                      document.getElementById('new-donation-type').value = '';
                      document.getElementById('new-donation-info').value = '';
                      showToast('Bætt við!');
                    }
                  }}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
                >
                  Bæta við
                </button>
              </div>
            </div>

            {/* Custom Payment Options */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Sérsniðnir greiðslumátar</h3>
              <p className="text-gray-600 mb-4">Merktu við til að sýna/fela greiðslumáta á styrkjasíðunni.</p>
              <div className="space-y-4 mb-6">
                {paymentOptions.map(opt => (
                  <div key={opt.id} className={`border rounded-lg p-4 ${opt.visible !== false ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-3">
                        <input 
                          type="checkbox" 
                          checked={opt.visible !== false} 
                          onChange={async () => {
                            const updated = paymentOptions.map(p => p.id === opt.id ? {...p, visible: p.visible === false ? true : false} : p);
                            setPaymentOptions(updated);
                            await saveConfigToDb('paymentOptions', updated);
                            showToast(opt.visible !== false ? 'Falið!' : 'Sýnilegt!');
                          }} 
                          className="w-5 h-5 text-red-600 rounded" 
                        />
                        <span className="font-bold">{opt.name}</span>
                        <span className={`text-xs px-2 py-1 rounded-full ${opt.visible !== false ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                          {opt.visible !== false ? 'Sýnilegt' : 'Falið'}
                        </span>
                      </div>
                      <button onClick={() => deletePaymentOption(opt.id)} className="text-red-600 hover:text-red-800">Eyða</button>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">{opt.type === 'html' ? 'HTML Widget' : 'Upplýsingar'}</div>
                    <pre className="text-xs bg-white p-2 rounded border overflow-x-auto max-h-20">{opt.content.substring(0, 150)}{opt.content.length > 150 ? '...' : ''}</pre>
                  </div>
                ))}
                {paymentOptions.length === 0 && <p className="text-gray-500 text-center py-4">Engir sérsniðnir greiðslumátar. Bættu við neðar.</p>}
              </div>
              <div className="border-t pt-6">
                <h4 className="font-bold text-gray-700 mb-4">Bæta við greiðslumáta:</h4>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <input type="text" value={newPaymentOption.name} onChange={(e) => setNewPaymentOption({...newPaymentOption, name: e.target.value})} placeholder="Nafn (t.d. Stripe, Vipps)" className="px-4 py-2 border rounded-lg" />
                    <select value={newPaymentOption.type} onChange={(e) => setNewPaymentOption({...newPaymentOption, type: e.target.value})} className="px-4 py-2 border rounded-lg">
                      <option value="info">Upplýsingar (texti)</option>
                      <option value="html">HTML Widget (embed kóði)</option>
                    </select>
                  </div>
                  <textarea value={newPaymentOption.content} onChange={(e) => setNewPaymentOption({...newPaymentOption, content: e.target.value})} placeholder={newPaymentOption.type === 'html' ? 'Límdu HTML widget kóða hér...' : 'Skrifaðu greiðsluupplýsingar...'} className="w-full px-4 py-3 border rounded-lg font-mono text-sm" rows={5} />
                  <button onClick={addPaymentOption} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700">Bæta við greiðslumáta</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Cookie Settings Tab */}
        {activeTab === 'cookies' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900"><i className="fas fa-cookie-bite mr-2"></i>Vafrakökustillingar</h2>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 font-medium">{cookieConfig.enabled ? 'Virkt' : 'Óvirkt'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only" checked={cookieConfig.enabled} onChange={(e) => setCookieConfig({...cookieConfig, enabled: e.target.checked})} />
                    <div className={`w-14 h-8 rounded-full transition ${cookieConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition transform ${cookieConfig.enabled ? 'translate-x-6' : ''}`}></div>
                  </div>
                </label>
              </div>
              
              <p className="text-gray-600 mb-6">Stilltu textann sem birtist í vafrakökuglugga. Þetta tryggir að vefsíðan þín sé í samræmi við GDPR og persónuverndarlög.</p>
              
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Titill</label>
                    <input type="text" value={cookieConfig.title} onChange={(e) => setCookieConfig({...cookieConfig, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Tengill á persónuvernd</label>
                    <input type="text" value={cookieConfig.privacyLinkText} onChange={(e) => setCookieConfig({...cookieConfig, privacyLinkText: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                  <textarea value={cookieConfig.description} onChange={(e) => setCookieConfig({...cookieConfig, description: e.target.value})} className="w-full px-4 py-3 border rounded-lg" rows={3} />
                </div>
                
                <div className="border-t pt-6">
                  <h3 className="font-bold text-gray-900 mb-4">Vafrakökuflokkar</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-lock text-green-600"></i>
                        <input type="text" value={cookieConfig.necessaryTitle} onChange={(e) => setCookieConfig({...cookieConfig, necessaryTitle: e.target.value})} className="font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:border-indigo-500 outline-none" />
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">Alltaf virkt</span>
                      </div>
                      <textarea value={cookieConfig.necessaryDesc} onChange={(e) => setCookieConfig({...cookieConfig, necessaryDesc: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-chart-bar text-blue-600"></i>
                        <input type="text" value={cookieConfig.analyticsTitle} onChange={(e) => setCookieConfig({...cookieConfig, analyticsTitle: e.target.value})} className="font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:border-indigo-500 outline-none" />
                      </div>
                      <textarea value={cookieConfig.analyticsDesc} onChange={(e) => setCookieConfig({...cookieConfig, analyticsDesc: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <i className="fas fa-bullhorn text-purple-600"></i>
                        <input type="text" value={cookieConfig.marketingTitle} onChange={(e) => setCookieConfig({...cookieConfig, marketingTitle: e.target.value})} className="font-bold text-gray-900 bg-transparent border-b border-gray-300 focus:border-indigo-500 outline-none" />
                      </div>
                      <textarea value={cookieConfig.marketingDesc} onChange={(e) => setCookieConfig({...cookieConfig, marketingDesc: e.target.value})} className="w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
                    </div>
                  </div>
                </div>
                
                <div className="border-t pt-6">
                  <h3 className="font-bold text-gray-900 mb-4">Hnappatexti</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-gray-700 text-sm mb-1">Samþykkja allt</label>
                      <input type="text" value={cookieConfig.acceptAllText} onChange={(e) => setCookieConfig({...cookieConfig, acceptAllText: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm mb-1">Aðeins nauðsynlegar</label>
                      <input type="text" value={cookieConfig.acceptNecessaryText} onChange={(e) => setCookieConfig({...cookieConfig, acceptNecessaryText: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-gray-700 text-sm mb-1">Vista stillingar</label>
                      <input type="text" value={cookieConfig.savePreferencesText} onChange={(e) => setCookieConfig({...cookieConfig, savePreferencesText: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                    </div>
                  </div>
                </div>
              </div>
              
              <button onClick={async () => { await saveConfigToDb('cookieConfig', cookieConfig); showToast('Vafrakökustillingar vistaðar!'); }} className="mt-6 bg-indigo-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-indigo-700">
                <i className="fas fa-save mr-2"></i>Vista stillingar
              </button>
            </div>
            
            {/* Preview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Forskoðun</h3>
              <div className="bg-gray-900 text-white p-6 rounded-lg max-w-md">
                <h4 className="text-lg font-bold mb-2">{cookieConfig.title}</h4>
                <p className="text-gray-300 text-sm mb-4">{cookieConfig.description}</p>
                <div className="flex gap-2 flex-wrap">
                  <button className="bg-indigo-600 text-white px-4 py-2 rounded text-sm">{cookieConfig.acceptAllText}</button>
                  <button className="bg-gray-700 text-white px-4 py-2 rounded text-sm">{cookieConfig.acceptNecessaryText}</button>
                </div>
                <p className="text-indigo-400 text-xs mt-3 underline cursor-pointer">{cookieConfig.privacyLinkText}</p>
              </div>
            </div>
          </div>
        )}

        {/* Privacy Policy Tab */}
        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900"><i className="fas fa-shield-alt mr-2"></i>Persónuverndarstefna</h2>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 font-medium">{privacyConfig.enabled ? 'Virkt' : 'Óvirkt'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only" checked={privacyConfig.enabled} onChange={(e) => setPrivacyConfig({...privacyConfig, enabled: e.target.checked})} />
                    <div className={`w-14 h-8 rounded-full transition ${privacyConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition transform ${privacyConfig.enabled ? 'translate-x-6' : ''}`}></div>
                  </div>
                </label>
              </div>
              
              <p className="text-gray-600 mb-6">Skrifaðu og breyttu persónuverndarstefnu vefsíðunnar. Tengill birtist í fæti síðunnar.</p>
              
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Titill</label>
                    <input type="text" value={privacyConfig.title} onChange={(e) => setPrivacyConfig({...privacyConfig, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                  <div>
                    <label className="block text-gray-700 font-medium mb-2">Síðast uppfært</label>
                    <input type="date" value={privacyConfig.lastUpdated} onChange={(e) => setPrivacyConfig({...privacyConfig, lastUpdated: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Efni persónuverndarstefnu</label>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 px-3 py-2 border-b flex gap-2 flex-wrap">
                      <button type="button" onClick={() => document.execCommand('bold')} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-bold"></i></button>
                      <button type="button" onClick={() => document.execCommand('italic')} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-italic"></i></button>
                      <button type="button" onClick={() => document.execCommand('underline')} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-underline"></i></button>
                      <span className="border-l mx-1"></span>
                      <button type="button" onClick={() => document.execCommand('insertUnorderedList')} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-list-ul"></i></button>
                      <button type="button" onClick={() => document.execCommand('insertOrderedList')} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-list-ol"></i></button>
                      <span className="border-l mx-1"></span>
                      <button type="button" onClick={() => { const url = prompt('Sláðu inn URL:'); if(url) document.execCommand('createLink', false, url); }} className="px-2 py-1 hover:bg-gray-200 rounded"><i className="fas fa-link"></i></button>
                    </div>
                    <iframe 
                      title="Privacy Editor"
                      className="w-full h-96"
                      ref={(iframe) => {
                        if (iframe && !iframe.dataset.init) {
                          iframe.dataset.init = 'true';
                          const doc = iframe.contentDocument;
                          doc.designMode = 'on';
                          doc.body.innerHTML = privacyConfig.content;
                          doc.body.style.fontFamily = 'system-ui, sans-serif';
                          doc.body.style.padding = '16px';
                          doc.body.style.lineHeight = '1.6';
                          doc.body.addEventListener('input', () => {
                            setPrivacyConfig(prev => ({...prev, content: doc.body.innerHTML}));
                          });
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <button onClick={async () => { await saveConfigToDb('privacyConfig', privacyConfig); showToast('Persónuverndarstefna vistuð!'); }} className="mt-6 bg-indigo-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-indigo-700">
                <i className="fas fa-save mr-2"></i>Vista persónuverndarstefnu
              </button>
            </div>
            
            {/* Preview */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="font-bold text-gray-900 mb-4">Forskoðun</h3>
              <div className="border rounded-lg p-6 max-h-96 overflow-y-auto prose prose-sm max-w-none">
                <p className="text-gray-500 text-sm mb-4">Síðast uppfært: {privacyConfig.lastUpdated}</p>
                <div dangerouslySetInnerHTML={{ __html: privacyConfig.content }} />
              </div>
            </div>
          </div>
        )}

        {/* System Tab */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Randalin Vefumsjónarkerfi</h2>
                <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">Útgáfa 0.1</span>
              </div>
              
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Um kerfið</h3>
                <p className="text-gray-700 mb-4">
                  Randalin er íslenskt vefumsjónarkerfi hannað af Target veflausnum. Þessi leyfis útgáfa tilheyrir GoRed Ísland.
                </p>
                <div className="bg-white/60 rounded-lg p-3 mt-4">
                  <p className="text-xs text-gray-500 mb-1">Leyfislykill:</p>
                  <code className="text-sm font-mono text-indigo-700">RNDL-GORD-2024-X7K9-M3PQ-WVBN</code>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">Eiginleikar í útgáfu 0.1</h4>
                  <ul className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Fréttir - Búa til, breyta, eyða</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Sögur - Persónulegar sögur</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Uppskriftir - Hollar uppskriftir</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Áhættuþættir - Breytanlegir</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Myndasöfn - Margar myndir í einu</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Valmynd - Sýna/fela hluta</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Fótur - Breyta tenglum og upplýsingum</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Notendastjórnun - Búa til notendur</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Styrkir - Breyta greiðsluupplýsingum</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Einkennaskoðari - Stillanlegt</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>AED Staðsetningar - Bæta við/eyða</li>
                    <li className="flex items-center"><i className="fas fa-check text-green-500 mr-2"></i>Fréttabréf - Sjá áskrifendur</li>
                  </ul>
                </div>
                <div className="bg-white border rounded-lg p-4">
                  <h4 className="font-bold text-gray-900 mb-3">📝 Leiðbeiningar</h4>
                  <div className="space-y-3 text-sm text-gray-600">
                    <div>
                      <p className="font-medium text-gray-800">🧭 Hliðarstika (Sidebar):</p>
                      <p>Notaðu hliðarstikuna vinstra megin til að fara á milli hluta. Smelltu á flokkana til að opna/loka þeim. Hægt er að minnka hliðarstikuna með örinni efst.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">📂 Efni (Fréttir, Sögur, Uppskriftir, Viðburðir):</p>
                      <p>Veldu viðeigandi flipa og smelltu á „Nýtt" til að bæta við efni. Notaðu ritilinn til að setja inn texta, myndir og tengla. Merktu við „Sýna höfund" til að birta nafn höfundar.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">📅 Viðburðir:</p>
                      <p>Bættu við viðburðum með dagsetningu, tíma (24 klst sniði), staðsetningu og GPS hnit. Hægt er að bæta við vefsíðu tengli. Viðburðir birtast á korti á forsíðu.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">🖼️ Myndir og Myndasöfn:</p>
                      <p>Hlaðið upp myndum beint í kerfið eða notið URL. Myndasöfn geta innihaldið margar myndir sem birtast í ljóskassa.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">👥 Notendur og Lykilorð:</p>
                      <p>Farðu í „Notendur" til að búa til nýja notendur. Til að breyta lykilorði, sláðu inn nýtt lykilorð og smelltu á „Breyta lykilorði". Notendur skrá sig inn með netfangi/notandanafni.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">⚙️ Valmynd og Sýnileiki:</p>
                      <p>Farðu í „Valmynd" til að sýna eða fela hluta á vefsíðunni. Merktu við til að sýna, afmerktu til að fela.</p>
                    </div>
                    <div>
                      <p className="font-medium text-gray-800">💝 Styrkir:</p>
                      <p>Stilltu greiðslumöguleika í „Styrkir" flipanum. Hægt er að sýna/fela einstaka valkosti og bæta við nýjum.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">📊 Tölfræði</h2>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-red-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-red-600">{news.length}</div><div className="text-gray-600">Fréttir</div></div>
                <div className="bg-blue-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-blue-600">{stories.length}</div><div className="text-gray-600">Sögur</div></div>
                <div className="bg-green-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-green-600">{recipes.length}</div><div className="text-gray-600">Uppskriftir</div></div>
                <div className="bg-yellow-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-yellow-600">{risks.length}</div><div className="text-gray-600">Áhættuþættir</div></div>
              </div>
              <div className="grid md:grid-cols-4 gap-4 mt-4">
                <div className="bg-purple-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-purple-600">{galleries.length}</div><div className="text-gray-600">Myndasöfn</div></div>
                <div className="bg-indigo-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-indigo-600">{adminUsers.length}</div><div className="text-gray-600">Notendur</div></div>
                <div className="bg-pink-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-pink-600">{magazines.length}</div><div className="text-gray-600">Tímarit</div></div>
                <div className="bg-orange-50 p-4 rounded-lg text-center"><div className="text-3xl font-bold text-orange-600">{menuItems.filter(m => m.visible).length}</div><div className="text-gray-600">Sýnilegir valmyndarliðir</div></div>
              </div>
            </div>
          </div>
        )}

        {/* Symptom Checker Tab */}
        {activeTab === 'symptom' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Einkennaskoðari - Stillingar</h2>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 font-medium">{symptomCheckerConfig.enabled ? 'Virkt' : 'Óvirkt'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only" checked={symptomCheckerConfig.enabled} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, enabled: e.target.checked})} />
                    <div className={`w-14 h-8 rounded-full transition ${symptomCheckerConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition transform ${symptomCheckerConfig.enabled ? 'translate-x-6' : ''}`}></div>
                  </div>
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Titill popup</label>
                  <input type="text" value={symptomCheckerConfig.title || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, title: e.target.value})} placeholder="Einkennaskoðari" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                  <input type="text" value={symptomCheckerConfig.description || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, description: e.target.value})} placeholder="Veldu einkenni sem þú finnur fyrir" className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Há áhætta titill</label>
                  <input type="text" value={symptomCheckerConfig.highRiskTitle || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, highRiskTitle: e.target.value})} placeholder="Há Áhætta" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Há áhætta texti</label>
                  <input type="text" value={symptomCheckerConfig.highRiskText || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, highRiskText: e.target.value})} placeholder="Ráðfærðust við lækni" className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">Niðurstöðutexti</label>
                <textarea value={symptomCheckerConfig.resultText || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, resultText: e.target.value})} placeholder="Þú hefur valið einkenni sem gott væri að ráðfærast við heilbrigðisstarfsmann um." className="w-full px-4 py-3 border rounded-lg" rows={2} />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">Neyðartexti</label>
                <textarea value={symptomCheckerConfig.emergencyText || ''} onChange={(e) => setSymptomCheckerConfig({...symptomCheckerConfig, emergencyText: e.target.value})} className="w-full px-4 py-3 border rounded-lg" rows={2} />
              </div>
              <button onClick={async () => { await saveConfigToDb('symptomCheckerConfig', symptomCheckerConfig); showToast('Stillingar vistaðar!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista stillingar</button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Einkenni</h3>
              <div className="space-y-3 mb-6">
                {(symptomCheckerConfig.symptoms || []).map((symptom) => (
                  <div key={symptom.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <span className="font-medium">{symptom.name}</span>
                      <span className={`ml-2 text-xs px-2 py-1 rounded ${symptom.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{symptom.severity === 'high' ? 'Há áhætta' : 'Miðlungs'}</span>
                      <p className="text-sm text-gray-500 mt-1">{symptom.description}</p>
                    </div>
                    <button onClick={() => setSymptomCheckerConfig({...symptomCheckerConfig, symptoms: symptomCheckerConfig.symptoms.filter(s => s.id !== symptom.id)})} className="text-red-500 hover:text-red-700"><i className="fas fa-trash"></i></button>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4">
                <h4 className="font-bold mb-3">Bæta við einkenni</h4>
                <div className="grid md:grid-cols-3 gap-3">
                  <input type="text" placeholder="Nafn" value={newSymptom.name} onChange={(e) => setNewSymptom({...newSymptom, name: e.target.value})} className="px-4 py-2 border rounded-lg" />
                  <select value={newSymptom.severity} onChange={(e) => setNewSymptom({...newSymptom, severity: e.target.value})} className="px-4 py-2 border rounded-lg">
                    <option value="high">Há áhætta</option>
                    <option value="medium">Miðlungs</option>
                  </select>
                  <input type="text" placeholder="Lýsing" value={newSymptom.description} onChange={(e) => setNewSymptom({...newSymptom, description: e.target.value})} className="px-4 py-2 border rounded-lg" />
                </div>
                <button onClick={() => {
                  if (newSymptom.name) {
                    setSymptomCheckerConfig({...symptomCheckerConfig, symptoms: [...(symptomCheckerConfig.symptoms || []), { ...newSymptom, id: Date.now() }]});
                    setNewSymptom({ name: '', severity: 'medium', description: '' });
                  }
                }} className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg">+ Bæta við</button>
              </div>
            </div>
          </div>
        )}

        {/* AED Locator Tab */}
        {activeTab === 'aed' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">AED Staðsetningar - Stillingar</h2>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 font-medium">{aedConfig.enabled ? 'Virkt' : 'Óvirkt'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only" checked={aedConfig.enabled} onChange={(e) => setAedConfig({...aedConfig, enabled: e.target.checked})} />
                    <div className={`w-14 h-8 rounded-full transition ${aedConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition transform ${aedConfig.enabled ? 'translate-x-6' : ''}`}></div>
                  </div>
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Titill popup</label>
                  <input type="text" value={aedConfig.title || ''} onChange={(e) => setAedConfig({...aedConfig, title: e.target.value})} placeholder="AED Staðsetningar" className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                  <input type="text" value={aedConfig.description || ''} onChange={(e) => setAedConfig({...aedConfig, description: e.target.value})} placeholder="AED (Automated External Defibrillator) getur bjargað lífi við hjartastopp." className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 font-medium mb-2">Undirtexti</label>
                <input type="text" value={aedConfig.subtitle || ''} onChange={(e) => setAedConfig({...aedConfig, subtitle: e.target.value})} placeholder="Finndu næsta tæki" className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <div className="mb-6">
                <label className="block text-gray-700 font-medium mb-2">Staðsetningar titill</label>
                <input type="text" value={aedConfig.locationsTitle || ''} onChange={(e) => setAedConfig({...aedConfig, locationsTitle: e.target.value})} placeholder="Staðsetningar" className="w-full px-4 py-3 border rounded-lg" />
              </div>
              <button onClick={async () => { await saveConfigToDb('aedConfig', aedConfig); showToast('Stillingar vistaðar!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista stillingar</button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">AED Staðsetningar ({(aedConfig.locations || []).length})</h3>
              <div className="space-y-3 mb-6">
                {(aedConfig.locations || []).map((loc) => (
                  <div key={loc.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div>
                      <span className="font-medium">{loc.name}</span>
                      <p className="text-sm text-gray-500">{loc.address}</p>
                      {loc.phone && <p className="text-sm text-gray-500">Sími: {loc.phone}</p>}
                    </div>
                    <button onClick={() => setAedConfig({...aedConfig, locations: aedConfig.locations.filter(l => l.id !== loc.id)})} className="text-red-500 hover:text-red-700"><i className="fas fa-trash"></i></button>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4">
                <h4 className="font-bold mb-3">Bæta við staðsetningu</h4>
                <div className="grid md:grid-cols-2 gap-3 mb-3">
                  <input type="text" placeholder="Nafn" value={newAedLocation.name} onChange={(e) => setNewAedLocation({...newAedLocation, name: e.target.value})} className="px-4 py-2 border rounded-lg" />
                  <input type="text" placeholder="Heimilisfang" value={newAedLocation.address} onChange={(e) => setNewAedLocation({...newAedLocation, address: e.target.value})} className="px-4 py-2 border rounded-lg" />
                  <input type="text" placeholder="Sími (valfrjálst)" value={newAedLocation.phone} onChange={(e) => setNewAedLocation({...newAedLocation, phone: e.target.value})} className="px-4 py-2 border rounded-lg" />
                  <input type="text" placeholder="GPS (valfrjálst)" value={newAedLocation.lat} onChange={(e) => setNewAedLocation({...newAedLocation, lat: e.target.value})} className="px-4 py-2 border rounded-lg" />
                </div>
                <button onClick={() => {
                  if (newAedLocation.name && newAedLocation.address) {
                    setAedConfig({...aedConfig, locations: [...(aedConfig.locations || []), { ...newAedLocation, id: Date.now() }]});
                    setNewAedLocation({ name: '', address: '', lat: '', lng: '', phone: '' });
                  }
                }} className="bg-green-600 text-white px-4 py-2 rounded-lg">+ Bæta við</button>
              </div>
            </div>
          </div>
        )}

        {/* Newsletter Tab */}
        {activeTab === 'newsletter' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Fréttabréf - Stillingar</h2>
                <label className="flex items-center cursor-pointer">
                  <span className="mr-3 font-medium">{newsletterConfig.enabled ? 'Virkt' : 'Óvirkt'}</span>
                  <div className="relative">
                    <input type="checkbox" className="sr-only" checked={newsletterConfig.enabled} onChange={(e) => setNewsletterConfig({...newsletterConfig, enabled: e.target.checked})} />
                    <div className={`w-14 h-8 rounded-full transition ${newsletterConfig.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <div className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition transform ${newsletterConfig.enabled ? 'translate-x-6' : ''}`}></div>
                  </div>
                </label>
              </div>
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Titill</label>
                  <input type="text" value={newsletterConfig.title} onChange={(e) => setNewsletterConfig({...newsletterConfig, title: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Lýsing</label>
                  <input type="text" value={newsletterConfig.description} onChange={(e) => setNewsletterConfig({...newsletterConfig, description: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Texti á takka</label>
                  <input type="text" value={newsletterConfig.buttonText} onChange={(e) => setNewsletterConfig({...newsletterConfig, buttonText: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-gray-700 font-medium mb-2">Skilaboð eftir skráningu</label>
                  <input type="text" value={newsletterConfig.successMessage} onChange={(e) => setNewsletterConfig({...newsletterConfig, successMessage: e.target.value})} className="w-full px-4 py-3 border rounded-lg" />
                </div>
              </div>
              <button onClick={async () => { await saveConfigToDb('newsletterConfig', newsletterConfig); showToast('Stillingar vistaðar!'); }} className="bg-red-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-red-700">Vista stillingar</button>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-900">Skráðir ({newsletterConfig.subscribers.length})</h3>
                {newsletterConfig.subscribers.length > 0 && (
                  <button onClick={() => {
                    const headers = ['Nafn', 'Netfang', 'Dagsetning'];
                    const rows = newsletterConfig.subscribers.map(s => [s.name || '', s.email, s.date]);
                    const csvContent = [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
                    const BOM = '\uFEFF';
                    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `gored_skradir_${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    showToast('Excel skrá sótt!');
                  }} className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 flex items-center gap-2">
                    <i className="fas fa-file-excel"></i> Sækja Excel
                  </button>
                )}
              </div>
              {newsletterConfig.subscribers.length === 0 ? (
                <p className="text-gray-500">Engir skráðir enn.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Nafn</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Netfang</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700">Dagsetning</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-700">Aðgerðir</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {newsletterConfig.subscribers.map((sub, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-3">{sub.name || '-'}</td>
                          <td className="px-4 py-3">{sub.email}</td>
                          <td className="px-4 py-3 text-gray-500">{sub.date}</td>
                          <td className="px-4 py-3 text-right">
                            <button onClick={async () => {
                              const updated = {...newsletterConfig, subscribers: newsletterConfig.subscribers.filter((_, idx) => idx !== i)};
                              setNewsletterConfig(updated);
                              await saveConfigToDb('newsletterConfig', updated);
                            }} className="text-red-500 hover:text-red-700"><i className="fas fa-trash"></i></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Magazines Tab */}
        {activeTab === 'magazines' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Veftímarit</h2>
            <div className="space-y-4 mb-6">
              {magazines.map((mag) => (
                <div key={mag.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <h4 className="font-bold">{mag.title}</h4>
                    <p className="text-sm text-gray-500">{mag.info}</p>
                    <a href={mag.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">{mag.url}</a>
                  </div>
                  <button onClick={() => deleteMagazine(mag.id)} className="text-red-500 hover:text-red-700"><i className="fas fa-trash"></i></button>
                </div>
              ))}
              {magazines.length === 0 && <p className="text-gray-500">Engin tímarit skráð.</p>}
            </div>
            <div className="border-t pt-6">
              <h3 className="font-bold mb-4">Bæta við tímariti</h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <input type="text" placeholder="Titill" id="mag-title" className="px-4 py-3 border rounded-lg" />
                <input type="text" placeholder="Lýsing" id="mag-info" className="px-4 py-3 border rounded-lg" />
                <input type="url" placeholder="URL (hlekkur)" id="mag-url" className="px-4 py-3 border rounded-lg" />
              </div>
              <button onClick={() => {
                const title = document.getElementById('mag-title').value;
                const info = document.getElementById('mag-info').value;
                const url = document.getElementById('mag-url').value;
                if (title && url) {
                  addMagazine({ title, info, url });
                  document.getElementById('mag-title').value = '';
                  document.getElementById('mag-info').value = '';
                  document.getElementById('mag-url').value = '';
                }
              }} className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold hover:bg-green-700">+ Bæta við</button>
            </div>
            </div>
          </div>
        )}
      </main>

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeEditModal}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
              <h3 className="text-xl font-bold">{editData.id ? 'Breyta' : 'Nýtt'}</h3>
              <button onClick={closeEditModal} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-gray-700 font-medium mb-2">Titill</label>
                <input type="text" value={editData.title || ''} onChange={(e) => setEditData(prev => ({...prev, title: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" /></div>
              {editModal !== 'risk' && (
                <div><label className="block text-gray-700 font-medium mb-2">Aðalmynd</label>
                  <div className="flex gap-2 mb-2">
                    <input type="url" value={editData.image_url || ''} onChange={(e) => setEditData(prev => ({...prev, image_url: e.target.value}))} className="flex-1 px-4 py-3 border rounded-lg" placeholder="https://... eða hlaða upp" />
                    <label className="px-4 py-3 bg-red-600 text-white rounded-lg cursor-pointer hover:bg-red-700 flex items-center">
                      <i className="fas fa-upload mr-2"></i>Hlaða upp
                      <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                        const file = e.target.files[0];
                        if (file) {
                          const formData = new FormData();
                          formData.append('image', file);
                          try {
                            const res = await fetch(`${BACKEND_URL}/api/upload`, { method: 'POST', body: formData });
                            const data = await res.json();
                            if (data.url) setEditData(prev => ({...prev, image_url: data.url}));
                          } catch (err) {
                            console.error('Upload error:', err);
                          }
                        }
                      }} />
                    </label>
                  </div>
                  {editData.image_url && <img src={editData.image_url} alt="" className="mt-2 h-32 object-cover rounded-lg" />}</div>
              )}
              {editModal === 'risk' && (
                <div><label className="block text-gray-700 font-medium mb-2">Tákn (Icon)</label>
                  <select value={editData.icon || 'fas fa-heart'} onChange={(e) => setEditData(prev => ({...prev, icon: e.target.value}))} className="w-full px-4 py-3 border rounded-lg">
                    {RISK_ICONS.map(icon => (<option key={icon.value} value={icon.value}>{icon.preview} {icon.label}</option>))}
                  </select>
                  <div className="mt-2 p-4 bg-gray-100 rounded-lg flex items-center">
                    <span className="text-gray-600 mr-3">Forskoðun:</span>
                    <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center"><i className={`${editData.icon || 'fas fa-heart'} text-red-600 text-xl`}></i></div>
                  </div></div>
              )}
              <div><label className="block text-gray-700 font-medium mb-2">{editModal === 'risk' ? 'Lýsing' : 'Efni'}</label>
                {editModal === 'risk' ? (
                  <textarea value={editData.description || ''} onChange={(e) => setEditData(prev => ({...prev, description: e.target.value}))} className="w-full px-4 py-3 border rounded-lg" rows={4} />
                ) : (
                  <RichTextEditor value={editData.content || ''} onChange={(content) => setEditData(prev => ({...prev, content}))} />
                )}</div>
              {editModal !== 'risk' && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <input type="checkbox" checked={editData.is_published !== false} onChange={(e) => setEditData(prev => ({...prev, is_published: e.target.checked}))} className="w-5 h-5 text-red-600 rounded" id="published" />
                    <label htmlFor="published" className="ml-3">Birta strax</label>
                  </div>
                  <div className="flex items-center">
                    <input type="checkbox" checked={editData.show_author !== false} onChange={(e) => setEditData(prev => ({...prev, show_author: e.target.checked}))} className="w-5 h-5 text-red-600 rounded" id="show_author" />
                    <label htmlFor="show_author" className="ml-3">Sýna höfund</label>
                  </div>
                </div>
              )}
              <button onClick={saveItem} className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700">Vista</button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg z-50">{toast}</div>}
      <style>{`.heart-beat { animation: heart-beat 1.3s ease-in-out infinite; display: inline-block; } @keyframes heart-beat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.15); } 28% { transform: scale(1); } 42% { transform: scale(1.15); } 70% { transform: scale(1); } }`}</style>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      </div>
    </div>
  );
}

// Public Site Component
function PublicSite() {
  const [siteData, setSiteData] = useState(null);
  const [news, setNews] = useState([]);
  const [risks, setRisks] = useState([]);
  const [stories, setStories] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [visibility, setVisibility] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('home');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showMagazineModal, setShowMagazineModal] = useState(false);
  const [showRandalinModal, setShowRandalinModal] = useState(false);
  const [donations, setDonations] = useState([]);
  const [magazines, setMagazines] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [footerData, setFooterData] = useState({});
  const [socialLinks, setSocialLinks] = useState([]);
  const [footerLinks, setFooterLinks] = useState([]);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  // New feature states
  const [symptomCheckerConfig, setSymptomCheckerConfig] = useState({ enabled: false, title: '', symptoms: [] });
  const [aedConfig, setAedConfig] = useState({ enabled: false, title: '', locations: [] });
  const [newsletterConfig, setNewsletterConfig] = useState({ enabled: false, title: '', subscribers: [] });
  const [showSymptomChecker, setShowSymptomChecker] = useState(false);
  const [showAedLocator, setShowAedLocator] = useState(false);
  const [showNewsletter, setShowNewsletter] = useState(false);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterName, setNewsletterName] = useState('');
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  // Additional config states
  const [riskSectionConfig, setRiskSectionConfig] = useState({ title: 'Áhættuþættir', subtitle: 'Þekktu áhættuþættina og verndaðu hjartað þitt', useGradient: true });
  const [aboutSectionConfig, setAboutSectionConfig] = useState({ title: 'Afhverju GoRed?', useGradient: true, text: '', imageUrl: '' });
  const [contactInfo, setContactInfo] = useState({ email: '', phone: '', address: '' });
  const [aboutUsPageConfig, setAboutUsPageConfig] = useState({ menuTitle: 'GoRed Ísland', enabled: true, content: '' });
  const [showAboutUsPage, setShowAboutUsPage] = useState(false);
  const [customPages, setCustomPages] = useState([]);
  // Blog and Sponsors
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogConfig, setBlogConfig] = useState({ enabled: false, menuTitle: 'Blog' });
  const [sponsors, setSponsors] = useState([]);
  const [sponsorsConfig, setSponsorsConfig] = useState({ enabled: false, title: 'Bakhjarlar okkar', subtitle: '' });
  const [currentCustomPage, setCurrentCustomPage] = useState(null);
  const [galleries, setGalleries] = useState([]);
  const [selectedGallery, setSelectedGallery] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [events, setEvents] = useState([]);
  const [eventsConfig, setEventsConfig] = useState({ enabled: false, menuTitle: 'Viðburðir' });
  const [selectedEvent, setSelectedEvent] = useState(null);
  // Cookie and Privacy states
  const [cookieConfig, setCookieConfig] = useState({ enabled: false });
  const [privacyConfig, setPrivacyConfig] = useState({ enabled: false, title: 'Persónuverndarstefna', content: '' });
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState({ necessary: true, analytics: false, marketing: false });
  const navigate = useNavigate();

  useEffect(() => {
    // Check if mobile
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, newsRes, risksRes, storiesRes, recipesRes, visRes, donRes, magRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/settings`), fetch(`${BACKEND_URL}/api/news`), fetch(`${BACKEND_URL}/api/risks`),
          fetch(`${BACKEND_URL}/api/stories`), fetch(`${BACKEND_URL}/api/recipes`), fetch(`${BACKEND_URL}/api/visibility`),
          fetch(`${BACKEND_URL}/api/donations`), fetch(`${BACKEND_URL}/api/magazines`)
        ]);
        const settings = await settingsRes.json();
        setSiteData(settings);
        setNews(await newsRes.json());
        setRisks(await risksRes.json());
        setStories(await storiesRes.json());
        setRecipes(await recipesRes.json());
        setVisibility(await visRes.json());
        setDonations(await donRes.json());
        setMagazines(await magRes.json());
        
        // Load all config from database settings
        if (settings.menuItems) setMenuItems(JSON.parse(settings.menuItems));
        if (settings.footerData) setFooterData(JSON.parse(settings.footerData));
        if (settings.socialLinks) setSocialLinks(JSON.parse(settings.socialLinks).filter(s => s.visible));
        if (settings.footerLinks) setFooterLinks(JSON.parse(settings.footerLinks).filter(l => l.visible));
        if (settings.paymentOptions) setPaymentOptions(JSON.parse(settings.paymentOptions).filter(p => p.visible));
        if (settings.symptomCheckerConfig) setSymptomCheckerConfig(JSON.parse(settings.symptomCheckerConfig));
        if (settings.aedConfig) setAedConfig(JSON.parse(settings.aedConfig));
        if (settings.newsletterConfig) setNewsletterConfig(JSON.parse(settings.newsletterConfig));
        if (settings.riskSectionConfig) setRiskSectionConfig(JSON.parse(settings.riskSectionConfig));
        if (settings.aboutSectionConfig) setAboutSectionConfig(JSON.parse(settings.aboutSectionConfig));
        if (settings.contactInfo) setContactInfo(JSON.parse(settings.contactInfo));
        if (settings.aboutUsPageConfig) setAboutUsPageConfig(JSON.parse(settings.aboutUsPageConfig));
        if (settings.customPages) setCustomPages(JSON.parse(settings.customPages));
        if (settings.galleries) setGalleries(JSON.parse(settings.galleries).filter(g => g.visible !== false));
        if (settings.events) setEvents(JSON.parse(settings.events));
        if (settings.eventsConfig) setEventsConfig(JSON.parse(settings.eventsConfig));
        if (settings.cookieConfig) setCookieConfig(JSON.parse(settings.cookieConfig));
        if (settings.privacyConfig) setPrivacyConfig(JSON.parse(settings.privacyConfig));
        if (settings.blogPosts) setBlogPosts(JSON.parse(settings.blogPosts));
        if (settings.blogConfig) setBlogConfig(JSON.parse(settings.blogConfig));
        if (settings.sponsors) setSponsors(JSON.parse(settings.sponsors));
        if (settings.sponsorsConfig) setSponsorsConfig(JSON.parse(settings.sponsorsConfig));
        
        // Check if cookie consent is needed
        const savedConsent = localStorage.getItem('cookieConsent');
        if (!savedConsent && JSON.parse(settings.cookieConfig || '{}').enabled) {
          setShowCookieConsent(true);
        } else if (savedConsent) {
          setCookiePreferences(JSON.parse(savedConsent));
        }
      } catch (e) { console.error('Error:', e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // Helper function to save config to database
  const saveConfigToDb = async (key, value) => {
    try {
      await fetch(`${BACKEND_URL}/api/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [key]: JSON.stringify(value) })
      });
      return true;
    } catch (e) {
      console.error('Save error:', e);
      return false;
    }
  };

  const openArticle = (type, item) => setSelectedArticle({ type, ...item });
  const closeArticle = () => setSelectedArticle(null);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 heart-beat">❤️</div>
          <h1 className="text-3xl font-bold mb-2 gradient-text">GoRed Ísland</h1>
          <p className="text-xl text-gray-500">Hleð síðu...</p>
        </div>
      </div>
    );
  }

  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow-lg sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <button onClick={closeArticle} className="text-gray-600 hover:text-red-600 flex items-center font-medium">
              <span className="mr-2 text-xl">←</span> Til baka
            </button>
          </div>
        </nav>
        <article className="max-w-4xl mx-auto px-4 py-8">
          {selectedArticle.image_url && <img src={selectedArticle.image_url} alt={selectedArticle.title} className="w-full h-64 md:h-96 object-cover rounded-2xl mb-8 shadow-lg" />}
          <h1 className="text-4xl font-bold text-gray-900 mb-6">{selectedArticle.title}</h1>
          {selectedArticle.show_author && selectedArticle.created_by && (
            <p className="text-sm text-gray-500 mb-6">Höfundur: {selectedArticle.created_by}</p>
          )}
          <div className="prose prose-lg max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: selectedArticle.content }} />
          
          {/* Gallery display if linked */}
          {selectedArticle.galleryId && (() => {
            const gallery = galleries.find(g => String(g.id) === String(selectedArticle.galleryId));
            if (gallery && (gallery.images || []).length > 0) {
              return (
                <div className="mt-12 border-t pt-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{gallery.name}</h2>
                  {gallery.description && <div className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: gallery.description }} />}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {(gallery.images || []).map(img => (
                      <img key={img.id} src={img.url} alt="" className="w-full h-48 object-cover rounded-lg shadow-md cursor-pointer hover:scale-105 transition" onClick={() => window.open(img.url, '_blank')} />
                    ))}
                  </div>
                </div>
              );
            }
            return null;
          })()}
        </article>
        <style>{`.heart-beat { animation: heart-beat 1.3s ease-in-out infinite; display: inline-block; } @keyframes heart-beat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.15); } 28% { transform: scale(1); } 42% { transform: scale(1.15); } 70% { transform: scale(1); } }`}</style>
      </div>
    );
  }

  const HeartIcon = ({ className = "" }) => <span className={`inline-block heart-beat ${className}`}>❤️</span>;
  
  const handleNavClick = (view) => {
    // Check if it's the gallery page
    if (view === '/gallery' || view === 'gallery') {
      setCurrentCustomPage(null);
      setCurrentView('gallery');
      setMobileMenuOpen(false);
      return;
    }
    // Check if it's a custom page
    const customPage = customPages.find(p => p.path === view);
    if (customPage) {
      setCurrentCustomPage(customPage);
      setCurrentView('custom');
    } else {
      setCurrentCustomPage(null);
      setCurrentView(view);
    }
    setMobileMenuOpen(false);
  };
  
  // Helper to check if a menu item should be shown
  const isMenuItemVisible = (path) => {
    const menuItem = menuItems.find(m => m.path === path);
    return menuItem ? menuItem.visible === true : false;
  };

  const tickerItems = [...news.filter(n => n.is_published !== false).map(n => ({ ...n, type: 'news' })), ...stories.filter(s => s.is_published !== false).map(s => ({ ...s, type: 'story' }))];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur shadow-lg">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex justify-between items-center h-16">
            <button onClick={() => handleNavClick('home')} className="flex items-center space-x-2">
              <HeartIcon className="text-2xl" />
              <span className="text-xl md:text-2xl font-bold text-red-600">GoRed Ísland</span>
            </button>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-2">
              {isMenuItemVisible('/') && <button onClick={() => handleNavClick('home')} className={`px-4 py-2 font-medium transition ${currentView === 'home' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/')?.label || 'Forsíða'}</button>}
              {aboutUsPageConfig.enabled && <button onClick={() => setShowAboutUsPage(true)} className="px-4 py-2 font-medium text-gray-700 hover:text-red-600 transition">{aboutUsPageConfig.menuTitle}</button>}
              {isMenuItemVisible('/news') && <button onClick={() => handleNavClick('news')} className={`px-4 py-2 font-medium transition ${currentView === 'news' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/news')?.label || 'Fréttir'}</button>}
              {isMenuItemVisible('/stories') && <button onClick={() => handleNavClick('stories')} className={`px-4 py-2 font-medium transition ${currentView === 'stories' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/stories')?.label || 'Sögur'}</button>}
              {isMenuItemVisible('/recipes') && <button onClick={() => handleNavClick('recipes')} className={`px-4 py-2 font-medium transition ${currentView === 'recipes' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/recipes')?.label || 'Uppskriftir'}</button>}
              {isMenuItemVisible('/gallery') && galleries.length > 0 && <button onClick={() => handleNavClick('gallery')} className={`px-4 py-2 font-medium transition ${currentView === 'gallery' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/gallery')?.label || 'Myndir'}</button>}
              {isMenuItemVisible('/events') && eventsConfig.enabled && events.length > 0 && <button onClick={() => handleNavClick('events')} className={`px-4 py-2 font-medium transition ${currentView === 'events' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{menuItems.find(m => m.path === '/events')?.label || 'Viðburðir'}</button>}
              {isMenuItemVisible('/blog') && blogConfig.enabled && <button onClick={() => handleNavClick('blog')} className={`px-4 py-2 font-medium transition ${currentView === 'blog' ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{blogConfig.menuTitle || 'Blogg'}</button>}
              {symptomCheckerConfig.enabled && <button onClick={() => setShowSymptomChecker(true)} className="px-4 py-2 font-medium text-gray-700 hover:text-red-600 transition">Einkenni</button>}
              {aedConfig.enabled && <button onClick={() => setShowAedLocator(true)} className="px-4 py-2 font-medium text-gray-700 hover:text-red-600 transition">AED</button>}
              {newsletterConfig.enabled && <button onClick={() => setShowNewsletter(true)} className="px-4 py-2 font-medium text-gray-700 hover:text-red-600 transition">Fréttabréf</button>}
              {menuItems.filter(m => !m.system && m.visible).map(item => (
                <button key={item.id} onClick={() => handleNavClick(item.path)} className={`px-4 py-2 font-medium transition ${currentView === item.path ? 'text-red-600 border-b-2 border-red-600' : 'text-gray-700 hover:text-red-600'}`}>{item.label}</button>
              ))}
            </div>
            
            <div className="flex items-center space-x-4">
              <button onClick={() => setShowDonationModal(true)} className="bg-red-600 text-white px-4 md:px-6 py-2 rounded-full font-semibold hover:bg-red-700 transition text-sm md:text-base">
                ❤️ Styrkja
              </button>
              
              {/* Mobile Menu Button */}
              {isMobile && (
                <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-gray-700 text-2xl p-2">
                  {mobileMenuOpen ? '✕' : '☰'}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMobile && mobileMenuOpen && (
          <div className="bg-white border-t shadow-lg">
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {isMenuItemVisible('/') && <button onClick={() => handleNavClick('home')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/')?.label || 'Forsíða'}</button>}
              {aboutUsPageConfig.enabled && <button onClick={() => { setShowAboutUsPage(true); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{aboutUsPageConfig.menuTitle}</button>}
              {isMenuItemVisible('/news') && <button onClick={() => handleNavClick('news')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/news')?.label || 'Fréttir'}</button>}
              {isMenuItemVisible('/stories') && <button onClick={() => handleNavClick('stories')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/stories')?.label || 'Sögur'}</button>}
              {isMenuItemVisible('/recipes') && <button onClick={() => handleNavClick('recipes')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/recipes')?.label || 'Uppskriftir'}</button>}
              {isMenuItemVisible('/gallery') && galleries.length > 0 && <button onClick={() => handleNavClick('gallery')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/gallery')?.label || 'Myndir'}</button>}
              {isMenuItemVisible('/events') && eventsConfig.enabled && events.length > 0 && <button onClick={() => handleNavClick('events')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{menuItems.find(m => m.path === '/events')?.label || 'Viðburðir'}</button>}
              {isMenuItemVisible('/blog') && blogConfig.enabled && <button onClick={() => handleNavClick('blog')} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{blogConfig.menuTitle || 'Blogg'}</button>}
              {symptomCheckerConfig.enabled && <button onClick={() => { setShowSymptomChecker(true); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-red-600">Einkennaskoðari</button>}
              {aedConfig.enabled && <button onClick={() => { setShowAedLocator(true); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-red-600">AED Staðsetningar</button>}
              {newsletterConfig.enabled && <button onClick={() => { setShowNewsletter(true); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium text-red-600">Fréttabréf</button>}
              {menuItems.filter(m => !m.system && m.visible).map(item => (
                <button key={item.id} onClick={() => handleNavClick(item.path)} className="block w-full text-left px-4 py-3 rounded-lg hover:bg-gray-100 font-medium">{item.label}</button>
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* News/Stories Ticker */}
      {tickerItems.length > 0 && (
        <div className="fixed top-16 left-0 right-0 bg-red-600 text-white py-2 overflow-hidden z-40">
          <div className="ticker-wrap"><div className="ticker">
            {[...tickerItems, ...tickerItems, ...tickerItems].map((item, i) => (
              <span key={i} className="ticker-item cursor-pointer hover:underline" onClick={() => openArticle(item.type, item)}>
                <span className="inline-block w-2 h-2 bg-white rounded-full mr-2"></span> {item.title}
              </span>
            ))}
          </div></div>
        </div>
      )}

      <main className="pt-28">
        {/* Custom Page View */}
        {currentView === 'custom' && currentCustomPage && (
          <section className="py-16 bg-white">
            <div className="max-w-4xl mx-auto px-4">
              <button onClick={() => handleNavClick('home')} className="mb-6 text-gray-600 hover:text-red-600 flex items-center">
                <span className="mr-2">←</span> Til baka
              </button>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">{currentCustomPage.title}</h1>
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: currentCustomPage.content }} />
            </div>
          </section>
        )}

        {/* Gallery Page View */}
        {currentView === 'gallery' && (
          <section className="py-16 bg-white">
            <div className="max-w-6xl mx-auto px-4">
              <button onClick={() => handleNavClick('home')} className="mb-6 text-gray-600 hover:text-red-600 flex items-center">
                <span className="mr-2">←</span> Til baka
              </button>
              <h1 className="text-4xl font-bold text-gray-900 mb-8">Myndasöfn</h1>
              {galleries.length === 0 ? (
                <p className="text-gray-500 text-center py-12">Engin myndasöfn til að sýna.</p>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {galleries.map(gallery => (
                    <div key={gallery.id} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer" onClick={() => { setSelectedGallery(gallery); }}>
                      {(gallery.images || []).length > 0 ? (
                        <img src={gallery.images[0].url} alt={gallery.name} className="w-full h-48 object-cover" />
                      ) : (
                        <div className="w-full h-48 bg-gray-200 flex items-center justify-center text-gray-400">Engar myndir</div>
                      )}
                      <div className="p-4">
                        <h3 className="font-bold text-lg text-gray-900">{gallery.name}</h3>
                        <p className="text-gray-500 text-sm">{(gallery.images || []).length} myndir</p>
                        {gallery.show_author && gallery.created_by && <p className="text-xs text-gray-400">Höfundur: {gallery.created_by}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Selected Gallery Modal */}
        {selectedGallery && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setSelectedGallery(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b p-4 flex justify-between items-center z-10">
                <h3 className="text-xl font-bold">{selectedGallery.name}</h3>
                <button onClick={() => setSelectedGallery(null)} className="text-gray-500 hover:text-gray-700 text-2xl">✕</button>
              </div>
              <div className="p-6">
                {selectedGallery.description && <div className="text-gray-600 mb-6" dangerouslySetInnerHTML={{ __html: selectedGallery.description }} />}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {(selectedGallery.images || []).map((img, idx) => (
                    <img 
                      key={img.id} 
                      src={img.url} 
                      alt="" 
                      className="w-full h-48 object-cover rounded-lg cursor-pointer hover:opacity-90 transition hover:scale-105" 
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setLightboxImages(selectedGallery.images || []);
                        setLightboxIndex(idx);
                        setLightboxImage(img.url);
                      }} 
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lightbox Modal */}
        {lightboxImage && (
          <div className="fixed inset-0 bg-black/95 z-[60] flex items-center justify-center" onClick={() => setLightboxImage(null)}>
            {/* Close button */}
            <button 
              onClick={() => setLightboxImage(null)} 
              className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 z-10"
            >
              ✕
            </button>
            
            {/* Image counter */}
            <div className="absolute top-4 left-4 text-white text-lg">
              {lightboxIndex + 1} / {lightboxImages.length}
            </div>
            
            {/* Previous button */}
            {lightboxImages.length > 1 && (
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  const newIdx = lightboxIndex === 0 ? lightboxImages.length - 1 : lightboxIndex - 1;
                  setLightboxIndex(newIdx);
                  setLightboxImage(lightboxImages[newIdx].url);
                }} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 p-4"
              >
                ‹
              </button>
            )}
            
            {/* Main image */}
            <img 
              src={lightboxImage} 
              alt="" 
              className="max-w-[90vw] max-h-[90vh] object-contain" 
              onClick={(e) => e.stopPropagation()}
            />
            
            {/* Next button */}
            {lightboxImages.length > 1 && (
              <button 
                onClick={(e) => { 
                  e.stopPropagation(); 
                  const newIdx = lightboxIndex === lightboxImages.length - 1 ? 0 : lightboxIndex + 1;
                  setLightboxIndex(newIdx);
                  setLightboxImage(lightboxImages[newIdx].url);
                }} 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white text-5xl hover:text-gray-300 p-4"
              >
                ›
              </button>
            )}
            
            {/* Thumbnail strip */}
            {lightboxImages.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 bg-black/50 p-2 rounded-lg max-w-[90vw] overflow-x-auto">
                {lightboxImages.map((img, idx) => (
                  <img 
                    key={img.id} 
                    src={img.url} 
                    alt="" 
                    className={`w-16 h-12 object-cover rounded cursor-pointer transition ${idx === lightboxIndex ? 'ring-2 ring-white opacity-100' : 'opacity-50 hover:opacity-75'}`}
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setLightboxIndex(idx);
                      setLightboxImage(img.url);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Events View */}
        {currentView === 'events' && eventsConfig.enabled && (
          <section className="py-8 md:py-12 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">{menuItems.find(m => m.path === '/events')?.label || 'Viðburðir'}</h1>
              
              {events.length === 0 ? (
                <p className="text-gray-500 text-center py-12">Engir viðburðir skráðir.</p>
              ) : (
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Events Calendar List - Næstu viðburðir */}
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Næstu viðburðir</h2>
                    <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                      {events.sort((a, b) => new Date(a.date) - new Date(b.date)).map(event => (
                        <div 
                          key={event.id} 
                          className="bg-white rounded-xl shadow-md p-4 cursor-pointer hover:shadow-lg transition border-l-4 border-red-500"
                          onClick={() => setSelectedEvent(event)}
                        >
                          <div className="flex items-start gap-4">
                            <div className="text-center bg-red-50 rounded-lg p-3 min-w-[70px]">
                              <div className="text-2xl font-bold text-red-600">{new Date(event.date).getDate()}</div>
                              <div className="text-xs text-gray-500 uppercase">{new Date(event.date).toLocaleDateString('is-IS', { month: 'short' })}</div>
                              <div className="text-xs text-gray-400">{new Date(event.date).getFullYear()}</div>
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold text-lg text-gray-900">{event.title}</h3>
                              {event.time && <p className="text-red-600 text-sm font-medium">kl. {event.time}</p>}
                              {event.location_name && <p className="text-gray-500 text-sm flex items-center gap-1"><i className="fas fa-map-marker-alt text-red-400"></i> {event.location_name}</p>}
                              {event.show_author && event.created_by && <p className="text-xs text-gray-400 mt-1">Höfundur: {event.created_by}</p>}
                            </div>
                            {event.image_url && <img src={event.image_url} alt="" className="w-20 h-20 object-cover rounded-lg" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Events Map - Shows all locations with Leaflet */}
                  <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Staðsetningar á korti</h2>
                    <div className="bg-white rounded-xl shadow-md overflow-hidden sticky top-24">
                      {/* Leaflet Map with all event pins */}
                      <div id="events-map" className="h-[500px] w-full" ref={(el) => {
                        if (el && !el.dataset.initialized && window.L) {
                          el.dataset.initialized = 'true';
                          const map = window.L.map(el).setView([64.1466, -21.9426], 6);
                          window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                            attribution: '© OpenStreetMap contributors'
                          }).addTo(map);
                          
                          const bounds = [];
                          events.forEach(event => {
                            const lat = parseFloat(event.latitude) || 64.1466;
                            const lng = parseFloat(event.longitude) || -21.9426;
                            bounds.push([lat, lng]);
                            
                            const marker = window.L.marker([lat, lng]).addTo(map);
                            const popupContent = `
                              <div style="min-width: 200px;">
                                <strong style="font-size: 14px;">${event.title}</strong><br/>
                                <span style="color: #dc2626;">${new Date(event.date).toLocaleDateString('is-IS')}${event.time ? ` kl. ${event.time}` : ''}</span><br/>
                                ${event.location_name ? `<span style="color: #666;"><i class="fas fa-map-marker-alt"></i> ${event.location_name}</span>` : ''}
                              </div>
                            `;
                            marker.bindPopup(popupContent);
                            marker.on('mouseover', function() { this.openPopup(); });
                          });
                          
                          if (bounds.length > 1) {
                            map.fitBounds(bounds, { padding: [20, 20] });
                          } else if (bounds.length === 1) {
                            map.setView(bounds[0], 10);
                          }
                        }
                      }}></div>
                      <div className="p-3 bg-gray-50 border-t text-center">
                        <p className="text-xs text-gray-500"><i className="fas fa-mouse-pointer mr-1"></i> Smelltu á nál til að sjá upplýsingar um viðburð</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Selected Event Modal */}
        {selectedEvent && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4" style={{ zIndex: 9999 }} onClick={() => setSelectedEvent(null)}>
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="relative">
                {selectedEvent.image_url && (
                  <img src={selectedEvent.image_url} alt={selectedEvent.title} className="w-full h-64 object-cover" />
                )}
                <button onClick={() => setSelectedEvent(null)} className="absolute top-4 right-4 bg-white/90 text-gray-700 w-10 h-10 rounded-full flex items-center justify-center text-xl hover:bg-white">✕</button>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-center bg-red-50 rounded-lg p-3">
                    <div className="text-3xl font-bold text-red-600">{new Date(selectedEvent.date).getDate()}</div>
                    <div className="text-sm text-gray-500">{new Date(selectedEvent.date).toLocaleDateString('is-IS', { month: 'short', year: 'numeric' })}</div>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.title}</h2>
                    {selectedEvent.time && <p className="text-red-600 font-medium">kl. {selectedEvent.time}</p>}
                  </div>
                </div>
                {selectedEvent.location_name && (
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <i className="fas fa-map-marker-alt text-red-500"></i>
                    <span>{selectedEvent.location_name}</span>
                  </div>
                )}
                {selectedEvent.website && (
                  <div className="flex items-center gap-2 text-gray-600 mb-4">
                    <i className="fas fa-globe text-blue-500"></i>
                    <a href={selectedEvent.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedEvent.website}</a>
                  </div>
                )}
                {selectedEvent.description && (
                  <div className="prose prose-lg text-gray-700 mb-4" dangerouslySetInnerHTML={{ __html: selectedEvent.description }} />
                )}
                {selectedEvent.show_author && selectedEvent.created_by && (
                  <p className="text-sm text-gray-400 mb-4">Höfundur: {selectedEvent.created_by}</p>
                )}
                {/* Mini map for this event */}
                {selectedEvent.latitude && selectedEvent.longitude && (
                  <div className="mt-4 rounded-lg overflow-hidden border" style={{ height: '200px' }}>
                    <iframe 
                      title="Event Location"
                      width="100%" 
                      height="100%" 
                      frameBorder="0" 
                      scrolling="no" 
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${selectedEvent.longitude - 0.01},${selectedEvent.latitude - 0.005},${selectedEvent.longitude + 0.01},${selectedEvent.latitude + 0.005}&layer=mapnik&marker=${selectedEvent.latitude},${selectedEvent.longitude}`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {currentView === 'home' && (
          <>
            {/* Hero Section */}
            <section className="bg-white py-16 md:py-32">
              <div className="max-w-7xl mx-auto px-4 text-center">
                <h1 className="text-4xl md:text-7xl font-extrabold mb-6">
                  <span className="gradient-text">{siteData?.heroTitle || 'GoRed'}</span>
                  <span className="text-gray-800"> {siteData?.heroSubtitle || 'fyrir'} </span>
                  <span className="gradient-text">{siteData?.heroHighlight || 'konur'}</span>
                  <span className="inline-block ml-2 md:ml-3 heart-beat text-4xl md:text-6xl">❤️</span>
                </h1>
                <p className="text-lg md:text-2xl max-w-3xl mx-auto mb-10 text-gray-600">{siteData?.heroDesc}</p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button onClick={() => setShowMagazineModal(true)} className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 md:px-8 py-3 md:py-4 rounded-full font-bold text-base md:text-lg hover:from-red-600 hover:to-pink-600 transition shadow-lg flex items-center gap-2">
                    <i className="fas fa-book-open"></i> Skoða tímarit
                  </button>
                </div>
              </div>
            </section>

            {/* About Section */}
            <section className="py-16 md:py-20 bg-gray-50">
              <div className="max-w-7xl mx-auto px-4">
                <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                  <div>
                    <h2 className={`text-3xl md:text-4xl font-bold mb-6 leading-tight pb-1 ${aboutSectionConfig.useGradient ? 'bg-gradient-to-r from-red-600 to-pink-500 bg-clip-text text-transparent' : 'text-gray-900'}`}>{aboutSectionConfig.title || siteData?.aboutTitle || 'Afhverju GoRed?'}</h2>
                    <div className="prose prose-lg text-gray-600" dangerouslySetInnerHTML={{ __html: aboutSectionConfig.text || siteData?.aboutText || '' }} />
                  </div>
                  <div><img src={aboutSectionConfig.imageUrl || siteData?.aboutImg || 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=600'} alt="Um GoRed" className="rounded-2xl shadow-xl w-full" /></div>
                </div>
              </div>
            </section>

            {/* Risk Factors */}
            <section className="py-16 md:py-20 bg-white">
              <div className="max-w-7xl mx-auto px-4">
                <div className="text-center mb-12">
                  <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${riskSectionConfig.useGradient ? 'bg-gradient-to-r from-red-600 to-pink-500 bg-clip-text text-transparent' : 'text-gray-900'}`}>{riskSectionConfig.title || '9 Áhættuþættir'}</h2>
                  <p className="text-lg md:text-xl text-gray-600">{riskSectionConfig.subtitle || 'Þekktu áhættuþættina og vernda hjarta þitt'}</p>
                </div>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
                  {risks.map((risk, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-4 md:p-6 shadow-lg border-l-4 border-red-600 hover:shadow-xl hover:-translate-y-1 transition">
                      <div className="flex items-center mb-4">
                        <div className="w-10 h-10 md:w-12 md:h-12 bg-red-100 rounded-full flex items-center justify-center mr-3 md:mr-4">
                          <i className={`${risk.icon} text-red-600 text-lg md:text-xl`}></i>
                        </div>
                        <h3 className="text-lg md:text-xl font-bold text-gray-900">{risk.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm md:text-base">{risk.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sponsors Section - Between Risks and News */}
            {sponsorsConfig.enabled && sponsors.length > 0 && (
              <section className="py-12 md:py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 text-center">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{sponsorsConfig.title || 'Bakhjarlar okkar'}</h2>
                  {sponsorsConfig.subtitle && <p className="text-lg text-gray-600 mb-8">{sponsorsConfig.subtitle}</p>}
                  <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
                    {sponsors.map(sponsor => (
                      <div key={sponsor.id} className="group">
                        {sponsor.website ? (
                          <a href={sponsor.website} target="_blank" rel="noopener noreferrer" className="block">
                            <img src={sponsor.logo_url} alt={sponsor.name} className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition opacity-70 hover:opacity-100" />
                          </a>
                        ) : (
                          <img src={sponsor.logo_url} alt={sponsor.name} className="h-12 md:h-16 object-contain grayscale hover:grayscale-0 transition opacity-70 hover:opacity-100" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Latest Blog Posts */}
            {blogConfig.enabled && blogPosts.length > 0 && (
              <section className="py-12 md:py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4">
                  <div className="flex justify-between items-center mb-8">
                    <h2 className="text-2xl md:text-4xl font-bold text-gray-900">{blogConfig.menuTitle || 'Blog'}</h2>
                    <button onClick={() => handleNavClick('blog')} className="text-red-600 hover:text-red-700 font-semibold text-sm md:text-base">Sjá allt →</button>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {blogPosts.slice(0, 3).map((post) => (
                      <div key={post.id} className="bg-gray-50 rounded-xl overflow-hidden shadow hover:shadow-lg transition">
                        {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-40 object-cover" />}
                        <div className="p-4">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h3>
                          {post.content && <p className="text-gray-600 text-sm mb-3">{post.content.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>}
                          {post.link_url ? (
                            <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="text-red-600 font-semibold text-sm">Lesa meira →</a>
                          ) : (
                            <button onClick={() => handleNavClick('blog')} className="text-red-600 font-semibold text-sm">Lesa meira →</button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* Latest News */}
            {visibility.news && news.length > 0 && (
              <section className="py-16 md:py-20 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4">
                  <div className="flex justify-between items-center mb-8 md:mb-12">
                    <h2 className="text-2xl md:text-4xl font-bold text-gray-900">Nýjustu fréttir</h2>
                    <button onClick={() => handleNavClick('news')} className="text-red-600 hover:text-red-700 font-semibold text-sm md:text-base">Sjá allar →</button>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                    {news.slice(0, 2).map((item) => (
                      <div key={item.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition cursor-pointer" onClick={() => openArticle('news', item)}>
                        {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-40 md:h-48 object-cover" />}
                        <div className="p-4 md:p-6">
                          <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                          <p className="text-gray-600 mb-4 text-sm md:text-base">{item.content?.replace(/<[^>]*>/g, '').substring(0, 150)}...</p>
                          <span className="text-red-600 font-semibold">Lesa meira →</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {/* News View */}
        {currentView === 'news' && (
          <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
            <button onClick={() => handleNavClick('home')} className="text-gray-600 hover:text-red-600 mb-6 md:mb-8">← Til baka</button>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 md:mb-8">Fréttir</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {news.map((item) => (
                <div key={item.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition cursor-pointer" onClick={() => openArticle('news', item)}>
                  {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-40 md:h-48 object-cover" />}
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{item.content?.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                    {item.show_author && item.created_by && <p className="text-xs text-gray-400 mb-2">Höfundur: {item.created_by}</p>}
                    <span className="text-red-600 font-semibold">Lesa meira →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stories View */}
        {currentView === 'stories' && (
          <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
            <button onClick={() => handleNavClick('home')} className="text-gray-600 hover:text-red-600 mb-6 md:mb-8">← Til baka</button>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Sögur</h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-12">Lestu sögur kvenna sem hafa upplifað hjartasjúkdóma</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {stories.map((story) => (
                <div key={story.id} className="bg-white rounded-xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition" onClick={() => openArticle('story', story)}>
                  {story.image_url && <img src={story.image_url} alt={story.title} className="w-full h-48 md:h-56 object-cover" />}
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{story.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{story.content?.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                    {story.show_author && story.created_by && <p className="text-xs text-gray-400 mb-2">Höfundur: {story.created_by}</p>}
                    <span className="text-red-600 font-semibold">Lesa meira →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recipes View */}
        {currentView === 'recipes' && (
          <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
            <button onClick={() => handleNavClick('home')} className="text-gray-600 hover:text-red-600 mb-6 md:mb-8">← Til baka</button>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Uppskriftir</h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-12">Hollar uppskriftir fyrir hjartað</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {recipes.map((recipe) => (
                <div key={recipe.id} className="bg-white rounded-xl overflow-hidden shadow-lg cursor-pointer hover:shadow-xl transition" onClick={() => openArticle('recipe', recipe)}>
                  {recipe.image_url && <img src={recipe.image_url} alt={recipe.title} className="w-full h-48 md:h-56 object-cover" />}
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{recipe.title}</h3>
                    <p className="text-gray-600 text-sm mb-3">{recipe.content?.replace(/<[^>]*>/g, '').substring(0, 100)}...</p>
                    {recipe.show_author && recipe.created_by && <p className="text-xs text-gray-400 mb-2">Höfundur: {recipe.created_by}</p>}
                    <span className="text-red-600 font-semibold">Sjá uppskrift →</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blog View */}
        {currentView === 'blog' && blogConfig.enabled && (
          <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
            <button onClick={() => handleNavClick('home')} className="text-gray-600 hover:text-red-600 mb-6 md:mb-8">← Til baka</button>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{blogConfig.menuTitle || 'Blog'}</h1>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {blogPosts.map((post) => (
                <div key={post.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition">
                  {post.image_url && <img src={post.image_url} alt={post.title} className="w-full h-48 md:h-56 object-cover" />}
                  <div className="p-4 md:p-6">
                    <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">{post.title}</h3>
                    {post.content && <div className="text-gray-600 text-sm mb-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: post.content.substring(0, 200) + '...' }} />}
                    {post.show_author && post.created_by && <p className="text-xs text-gray-400 mb-2">Höfundur: {post.created_by}</p>}
                    {post.link_url && (
                      <a href={post.link_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-red-600 font-semibold hover:text-red-700">
                        <i className="fas fa-external-link-alt mr-2"></i>Skoða nánar →
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {blogPosts.length === 0 && <p className="text-center text-gray-500 py-12">Engar bloggfærslur ennþá.</p>}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div>
              <div className="flex items-center space-x-2 mb-6">
                <span className="text-2xl heart-beat">❤️</span>
                <span className="text-xl md:text-2xl font-bold">{footerData.companyName || 'GoRed Ísland'}</span>
              </div>
              <p className="text-gray-400">{footerData.tagline || 'Hjartavernd á Íslandi síðan 2009'}</p>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">{footerData.linksTitle || 'Flýtitenglar'}</h4>
              <ul className="space-y-2">
                {(footerLinks.length > 0 ? footerLinks : [
                  { label: 'Forsíða', url: '/' }, { label: 'Fréttir', url: '/news' }, { label: 'Sögur', url: '/stories' }, { label: 'Uppskriftir', url: '/recipes' }
                ]).map((link, i) => (
                  <li key={i}><button onClick={() => handleNavClick(link.url === '/' ? 'home' : link.url.replace('/', ''))} className="text-gray-400 hover:text-white">{link.label}</button></li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">{footerData.contactTitle || 'Hafa samband'}</h4>
              <ul className="space-y-2 text-gray-400">
                {contactInfo.email && <li><i className="fas fa-envelope mr-2 text-red-500"></i>{contactInfo.email}</li>}
                {contactInfo.phone && <li><i className="fas fa-phone mr-2 text-red-500"></i>{contactInfo.phone}</li>}
                {contactInfo.address && <li><i className="fas fa-map-marker-alt mr-2 text-red-500"></i>{contactInfo.address}</li>}
                {!contactInfo.email && !contactInfo.phone && !contactInfo.address && (
                  <>
                    {footerData.email && <li><i className="fas fa-envelope mr-2 text-red-500"></i>{footerData.email}</li>}
                    {footerData.phone && <li><i className="fas fa-phone mr-2 text-red-500"></i>{footerData.phone}</li>}
                  </>
                )}
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">{footerData.socialTitle || 'Fylgstu með'}</h4>
              <div className="flex flex-wrap gap-3">
                {(socialLinks.length > 0 ? socialLinks : [
                  { icon: 'fab fa-facebook', url: '#' }, { icon: 'fab fa-instagram', url: '#' }, { icon: 'fab fa-twitter', url: '#' }
                ]).map((social, i) => (
                  <a key={i} href={social.url} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-600 transition">
                    <i className={social.icon}></i>
                  </a>
                ))}
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 md:mt-12 pt-8 text-center text-gray-400">
            <p>
              {footerData.copyright || `© ${new Date().getFullYear()} GoRed Ísland. Öll réttindi áskilin.`}
              {privacyConfig.enabled && (
                <> · <button onClick={() => setShowPrivacyPolicy(true)} className="text-gray-400 hover:text-white transition"><i className="fas fa-shield-alt mr-1"></i>{privacyConfig.title}</button></>
              )}
              {cookieConfig.enabled && (
                <> · <button onClick={() => { setShowCookieConsent(true); setShowCookieSettings(true); }} className="text-gray-400 hover:text-white transition"><i className="fas fa-cookie-bite mr-1"></i>Vafrakökur</button></>
              )}
              <> · <button onClick={() => setShowRandalinModal(true)} className="text-indigo-400 hover:text-indigo-300 transition">Randalin</button></>
            </p>
          </div>
        </div>
      </footer>

      {/* Randalin Modal */}
      {showRandalinModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRandalinModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl flex justify-between items-center">
              <div className="flex-1 text-center">
                <h3 className="text-xl font-bold">Randalin Vefumsjónarkerfi</h3>
              </div>
              <button onClick={() => setShowRandalinModal(false)} className="text-2xl hover:opacity-75 ml-4">✕</button>
            </div>
            <div className="p-6 text-center">
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="text-gray-700 font-medium mb-2">Hannað af:</p>
                <p className="text-gray-900">Vilhjálmur Á. Theódórsson</p>
                <p className="text-gray-900">&</p>
                <p className="text-gray-900">Magnús P. Guðmundsson</p>
              </div>
              <a href="https://target.is" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-indigo-600 hover:text-indigo-800 font-medium">
                Target.is veflausnir
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDonationModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold">💝 Styrkja GoRed</h3>
              <button onClick={() => setShowDonationModal(false)} className="text-2xl">✕</button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-gray-600 mb-6">Þakka þér fyrir að vilja styrkja GoRed átakið.</p>
              {donations.filter(d => d.is_active && d.donation_type === 'bank').map((d, i) => (
                <div key={i} className="p-4 border rounded-lg mb-4">
                  <h4 className="font-bold mb-2">🏦 Bankaupplýsingar</h4>
                  <pre className="text-gray-600 text-sm whitespace-pre-wrap">{d.info}</pre>
                </div>
              ))}
              {/* Custom Payment Options with HTML support */}
              {paymentOptions.filter(opt => opt.visible !== false).map((opt, i) => (
                <div key={i} className="p-4 border rounded-lg mb-4">
                  <h4 className="font-bold mb-2">💳 {opt.name}</h4>
                  {opt.type === 'html' ? (
                    <div dangerouslySetInnerHTML={{ __html: opt.content }} />
                  ) : (
                    <pre className="text-gray-600 text-sm whitespace-pre-wrap">{opt.content}</pre>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Magazine Modal */}
      {showMagazineModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowMagazineModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold"><i className="fas fa-book-open mr-2"></i>GoRed Timarit</h3>
              <button onClick={() => setShowMagazineModal(false)} className="text-2xl">X</button>
            </div>
            <div className="p-4 md:p-6">
              {magazines.map((m, i) => (
                <a key={i} href={m.url} target="_blank" rel="noopener noreferrer" className="block p-4 border rounded-lg mb-4 hover:border-red-400 hover:bg-red-50 transition">
                  <h4 className="font-bold">{m.title}</h4>
                  <p className="text-gray-600 text-sm">{m.info}</p>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Symptom Checker Modal */}
      {showSymptomChecker && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowSymptomChecker(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold"><i className="fas fa-heartbeat mr-2"></i>{symptomCheckerConfig.title || 'Einkennaskodari'}</h3>
              <button onClick={() => setShowSymptomChecker(false)} className="text-2xl">X</button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-gray-600 mb-6">{symptomCheckerConfig.description}</p>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-700 font-medium"><i className="fas fa-exclamation-triangle mr-2"></i>{symptomCheckerConfig.emergencyText}</p>
              </div>

              <h4 className="font-bold text-lg mb-4">Veldu einkenni sem thu finnur fyrir:</h4>
              <div className="space-y-3 mb-6">
                {symptomCheckerConfig.symptoms.map((symptom) => (
                  <label key={symptom.id} className={`flex items-start p-4 border rounded-lg cursor-pointer transition ${selectedSymptoms.includes(symptom.id) ? 'border-red-500 bg-red-50' : 'hover:border-gray-400'}`}>
                    <input type="checkbox" className="mt-1 mr-3 w-5 h-5 text-red-600" checked={selectedSymptoms.includes(symptom.id)} onChange={(e) => {
                      if (e.target.checked) setSelectedSymptoms([...selectedSymptoms, symptom.id]);
                      else setSelectedSymptoms(selectedSymptoms.filter(id => id !== symptom.id));
                    }} />
                    <div>
                      <span className="font-medium">{symptom.name}</span>
                      {symptom.severity === 'high' && <span className="ml-2 text-xs px-2 py-1 rounded bg-red-100 text-red-700">Ha ahaetta</span>}
                      <p className="text-sm text-gray-500 mt-1">{symptom.description}</p>
                    </div>
                  </label>
                ))}
              </div>

              {selectedSymptoms.length > 0 && (
                <div className={`p-4 rounded-lg mb-4 ${selectedSymptoms.some(id => symptomCheckerConfig.symptoms.find(s => s.id === id)?.severity === 'high') ? 'bg-red-100 border-2 border-red-500' : 'bg-yellow-50 border border-yellow-300'}`}>
                  {selectedSymptoms.some(id => symptomCheckerConfig.symptoms.find(s => s.id === id)?.severity === 'high') ? (
                    <div className="text-red-700">
                      <p className="font-bold text-lg mb-2"><i className="fas fa-exclamation-circle mr-2"></i>Hringdu strax i 112!</p>
                      <p>Thu hefur valid einkenni sem geta bent til hjartasjukdoms. Leitadu strax laeknis.</p>
                    </div>
                  ) : (
                    <div className="text-yellow-700">
                      <p className="font-bold mb-2"><i className="fas fa-info-circle mr-2"></i>Radferdust vid laekni</p>
                      <p>Thu hefur valid einkenni sem gott vaeri ad radferdast vid heilbrigdisstarfsmann um.</p>
                    </div>
                  )}
                </div>
              )}

              <a href="tel:112" className="block w-full bg-red-600 text-white text-center py-4 rounded-lg font-bold text-lg hover:bg-red-700 transition">
                <i className="fas fa-phone mr-2"></i>Hringja i 112
              </a>
            </div>
          </div>
        </div>
      )}

      {/* AED Locator Modal */}
      {showAedLocator && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAedLocator(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold"><i className="fas fa-map-marker-alt mr-2"></i>{aedConfig.title || 'AED Stadsetningar'}</h3>
              <button onClick={() => setShowAedLocator(false)} className="text-2xl">X</button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-gray-600 mb-6">{aedConfig.description}</p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                <p className="text-blue-700"><i className="fas fa-info-circle mr-2"></i>{aedConfig.description || 'AED (Automated External Defibrillator) getur bjargað mannslífum.'}</p>
              </div>

              <h4 className="font-bold text-lg mb-4">Stadsetningar ({aedConfig.locations.length})</h4>
              <div className="space-y-3">
                {aedConfig.locations.map((loc) => (
                  <div key={loc.id} className="p-4 border rounded-lg hover:border-red-400 transition">
                    <div className="flex items-start justify-between">
                      <div>
                        <h5 className="font-bold text-lg">{loc.name}</h5>
                        <p className="text-gray-600"><i className="fas fa-map-marker-alt mr-2 text-red-500"></i>{loc.address}</p>
                        {loc.phone && <p className="text-gray-600"><i className="fas fa-phone mr-2 text-green-500"></i>{loc.phone}</p>}
                      </div>
                      {loc.lat && loc.lng && (
                        <a href={`https://www.google.com/maps?q=${loc.lat},${loc.lng}`} target="_blank" rel="noopener noreferrer" className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-600">
                          <i className="fas fa-directions mr-1"></i>Leidarvisir
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {aedConfig.locations.length === 0 && (
                <p className="text-gray-500 text-center py-8">Engar stadsetningar skradar.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Newsletter Modal */}
      {showNewsletter && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowNewsletter(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full" onClick={e => e.stopPropagation()}>
            <div className="bg-red-600 text-white p-4 md:p-6 flex justify-between items-center">
              <h3 className="text-xl md:text-2xl font-bold"><i className="fas fa-envelope mr-2"></i>{newsletterConfig.title || 'Fréttabréf'}</h3>
              <button onClick={() => setShowNewsletter(false)} className="text-2xl">X</button>
            </div>
            <div className="p-4 md:p-6">
              <p className="text-gray-600 mb-6">{newsletterConfig.description}</p>
              
              {newsletterSuccess ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                  <i className="fas fa-check-circle text-green-500 text-4xl mb-3"></i>
                  <p className="text-green-700 font-medium">{newsletterConfig.successMessage}</p>
                </div>
              ) : (
                <div>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">Nafn</label>
                    <input type="text" value={newsletterName} onChange={(e) => setNewsletterName(e.target.value)} placeholder="Fullt nafn" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <div className="mb-4">
                    <label className="block text-gray-700 font-medium mb-2">Netfang</label>
                    <input type="email" value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} placeholder="netfang@daemi.is" className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500" />
                  </div>
                  <button onClick={async () => {
                    if (newsletterEmail && newsletterEmail.includes('@')) {
                      const updated = {...newsletterConfig, subscribers: [...newsletterConfig.subscribers, { name: newsletterName, email: newsletterEmail, date: new Date().toLocaleDateString('is-IS') }]};
                      setNewsletterConfig(updated);
                      await saveConfigToDb('newsletterConfig', updated);
                      setNewsletterSuccess(true);
                      setTimeout(() => { setShowNewsletter(false); setNewsletterSuccess(false); setNewsletterEmail(''); setNewsletterName(''); }, 3000);
                    }
                  }} className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 transition">
                    {newsletterConfig.buttonText || 'Skrá mig'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* About Us Page Modal */}
      {showAboutUsPage && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAboutUsPage(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-red-600 to-pink-500 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold">{aboutUsPageConfig.menuTitle}</h3>
              <button onClick={() => setShowAboutUsPage(false)} className="text-2xl hover:opacity-75">X</button>
            </div>
            <div className="p-6 md:p-8 prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: aboutUsPageConfig.content }} />
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyPolicy && privacyConfig.enabled && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowPrivacyPolicy(false)}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="bg-gray-900 text-white p-4 md:p-6 flex justify-between items-center sticky top-0">
              <h3 className="text-xl md:text-2xl font-bold"><i className="fas fa-shield-alt mr-2"></i>{privacyConfig.title}</h3>
              <button onClick={() => setShowPrivacyPolicy(false)} className="text-2xl hover:opacity-75">✕</button>
            </div>
            <div className="p-6 md:p-8">
              <p className="text-gray-500 text-sm mb-6">Síðast uppfært: {privacyConfig.lastUpdated}</p>
              <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: privacyConfig.content }} />
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      {showCookieConsent && cookieConfig.enabled && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 text-white p-4 md:p-6 z-50 shadow-2xl">
          <div className="max-w-6xl mx-auto">
            {!showCookieSettings ? (
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <h4 className="text-lg font-bold mb-2"><i className="fas fa-cookie-bite mr-2"></i>{cookieConfig.title}</h4>
                  <p className="text-gray-300 text-sm">{cookieConfig.description}</p>
                  <button onClick={() => setShowPrivacyPolicy(true)} className="text-indigo-400 hover:text-indigo-300 text-sm mt-2 underline">
                    {cookieConfig.privacyLinkText}
                  </button>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button onClick={() => {
                    const prefs = { necessary: true, analytics: true, marketing: true };
                    setCookiePreferences(prefs);
                    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
                    setShowCookieConsent(false);
                  }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition">
                    {cookieConfig.acceptAllText}
                  </button>
                  <button onClick={() => {
                    const prefs = { necessary: true, analytics: false, marketing: false };
                    setCookiePreferences(prefs);
                    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
                    setShowCookieConsent(false);
                  }} className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition">
                    {cookieConfig.acceptNecessaryText}
                  </button>
                  <button onClick={() => setShowCookieSettings(true)} className="border border-gray-600 hover:bg-gray-800 text-white px-6 py-3 rounded-lg font-medium transition">
                    <i className="fas fa-cog mr-1"></i> Stillingar
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-bold"><i className="fas fa-sliders-h mr-2"></i>Vafrakökustillingar</h4>
                  <button onClick={() => setShowCookieSettings(false)} className="text-gray-400 hover:text-white">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-lock text-green-500"></i>
                        <span className="font-medium">{cookieConfig.necessaryTitle}</span>
                        <span className="text-xs bg-green-900 text-green-300 px-2 py-0.5 rounded">Alltaf virkt</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{cookieConfig.necessaryDesc}</p>
                    </div>
                    <input type="checkbox" checked={true} disabled className="w-5 h-5 rounded" />
                  </div>
                  <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-chart-bar text-blue-500"></i>
                        <span className="font-medium">{cookieConfig.analyticsTitle}</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{cookieConfig.analyticsDesc}</p>
                    </div>
                    <input type="checkbox" checked={cookiePreferences.analytics} onChange={(e) => setCookiePreferences({...cookiePreferences, analytics: e.target.checked})} className="w-5 h-5 rounded accent-indigo-600" />
                  </div>
                  <div className="flex items-start justify-between p-4 bg-gray-800 rounded-lg">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <i className="fas fa-bullhorn text-purple-500"></i>
                        <span className="font-medium">{cookieConfig.marketingTitle}</span>
                      </div>
                      <p className="text-gray-400 text-sm mt-1">{cookieConfig.marketingDesc}</p>
                    </div>
                    <input type="checkbox" checked={cookiePreferences.marketing} onChange={(e) => setCookiePreferences({...cookiePreferences, marketing: e.target.checked})} className="w-5 h-5 rounded accent-indigo-600" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => {
                    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
                    setShowCookieConsent(false);
                    setShowCookieSettings(false);
                  }} className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition">
                    {cookieConfig.savePreferencesText}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cookie Settings Button (always visible after consent) */}
      {!showCookieConsent && cookieConfig.enabled && localStorage.getItem('cookieConsent') && (
        <button 
          onClick={() => { setShowCookieConsent(true); setShowCookieSettings(true); }}
          className="fixed bottom-4 left-4 bg-gray-800 hover:bg-gray-700 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center z-40 transition"
          title="Vafrakökustillingar"
        >
          <i className="fas fa-cookie-bite"></i>
        </button>
      )}

      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css" />
      <style>{`
        .gradient-text { background: linear-gradient(135deg, #dc2626 0%, #ec4899 50%, #f472b6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        @keyframes heart-beat { 0%, 100% { transform: scale(1); } 14% { transform: scale(1.15); } 28% { transform: scale(1); } 42% { transform: scale(1.15); } 70% { transform: scale(1); } }
        .heart-beat { display: inline-block; animation: heart-beat 1.3s ease-in-out infinite; }
        @keyframes pulse-red { 0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); } 50% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); } }
        .pulse-button { animation: pulse-red 2s infinite; }
        .ticker-wrap { width: 100%; overflow: hidden; }
        .ticker { display: inline-flex; animation: ticker 40s linear infinite; }
        .ticker:hover { animation-play-state: paused; }
        .ticker-item { padding: 0 2rem; white-space: nowrap; }
        @keyframes ticker { 0% { transform: translateX(0); } 100% { transform: translateX(-33.33%); } }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PublicSite />} />
        <Route path="/admin" element={<AdminPanel />} />
        <Route path="/admin.php" element={<AdminPanel />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
